import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Card, Container, Row, Col, Spinner } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import useOrderAlerts from "../hooks/useOrderAlerts";
import io from "socket.io-client";
import newOrderAudioNotification from "./../assets/img/newOrderNotification.mp3";
import { getUserFromLocalStorage } from "../utilities/user.js";
import { getTokenFromLocalStorage } from "../utilities/tokenUtils.js";
import { getActiveCountry } from "../utilities/activeCountry.js";
import { apiGetAnalyticsOverview, apiGetAnalyticsTrend, apiGetVendorAnalytics, apiGetAnalyticsRealtime, apiGetVendorShopPath, apiGetDashboardStats } from "../api/index";
import ChatAnalytics from "../components/ChatAnalytics.js";
import PlatformSalesAnalytics from "../components/PlatformSalesAnalytics";
import PlatformMoneyFlow from "../components/money/PlatformMoneyFlow";
import { CARD_FILTERS, cardLink, cardLinkProps } from "../utilities/orderDeepLinks";
import { formatAvgDelivery } from "../utilities/deliveryTime";

const InfoTip = ({ text }) => (
  <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 5, cursor: 'default', verticalAlign: 'middle' }} className="infotip-wrap">
    <i className="fas fa-info-circle" style={{ fontSize: '0.72rem', color: '#6b7a99', transition: 'color 0.15s' }}></i>
    <span className="infotip-bubble">{text}</span>
  </span>
);

const Dashboard = () => {
  const user = getUserFromLocalStorage();
  const userType = user?.result?.userType;
  const operationMode = user?.result?.operationMode;
  const userRole = user?.result?.user_role;

  const isLusukuStaff = userType === "LUSUKU STAFF";
  // Also check 'role' — authController stamps the JWT with 'role', profile has 'user_role'
  const isSuperAdmin  = isLusukuStaff && (userRole === "SUPERUSER" || user?.result?.role === "SUPERUSER");
  const isSupervisor  = isLusukuStaff && userRole === "SUPERVISOR";
  const isRestaurant  = !isLusukuStaff && operationMode === "RESTAURANT";
  const isHealthcare  = !isLusukuStaff && operationMode === "HEALTHCARE";
  const isRetailer    = !isLusukuStaff && (operationMode === "RETAILER" || operationMode === "WHOLESALER" || operationMode === "ONLINE" || userType === "SELLER");
  const is3PLPartner  = !isLusukuStaff && operationMode === "3PL_PARTNER";
  const is3PLStaff    = !isLusukuStaff && operationMode === "3PL_STAFF";
  const isBrandOwner  = !isLusukuStaff && operationMode === "BRAND_OWNER";
  const isVendor      = isRestaurant || isHealthcare || isRetailer;
  const agentId       = user?.result?._id;
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    processingCount: 0,
    dispatchedCount: 0,
    assignedCount: 0,
    deliveredCount: 0,
    codCount: 0,
    momoCount: 0,
  });
  const [socketConnected, setSocketConnected] = useState(false);

  // ── GA4 Analytics state ───────────────────────────────────────────────────
  const [analyticsTab, setAnalyticsTab] = useState('realtime'); // 'overview' | 'realtime'
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);

  // ── GA4 Realtime state ────────────────────────────────────────────────────
  const [realtimeData, setRealtimeData] = useState(null);
  const [realtimeLoading, setRealtimeLoading] = useState(true);
  const [realtimeError, setRealtimeError] = useState(null);
  const [realtimeLastUpdated, setRealtimeLastUpdated] = useState(null);

  // ── Vendor analytics state ────────────────────────────────────────────────
  const [vendorAnalyticsData, setVendorAnalyticsData] = useState(null);
  const [vendorAnalyticsLoading, setVendorAnalyticsLoading] = useState(false);
  const [vendorAnalyticsError, setVendorAnalyticsError] = useState(null);
  const [vendorAnalyticsDays, setVendorAnalyticsDays] = useState(30);
  // Resolved server-side: uses the actual vendor model _id (not Users._id)
  const [vendorShopPath, setVendorShopPath] = useState(null);

  // Live socket handle + the country it was last told about, so a super-admin's
  // country switch can re-scope the dashboard WITHOUT tearing the socket down.
  const socketRef = useRef(null);
  const joinedCountryRef = useRef(null);

  // ── In-house order: fired via global Admin.js modal (no local state needed) ──

  // Unified, reliable order-alert engine shared by every dashboard: owns the
  // audio unlock, the hidden-tab desktop notification, and the Web Push
  // subscription. Replaces the old per-file AudioContext + useSound duplication.
  const alerts = useOrderAlerts({ soundUrl: newOrderAudioNotification });
  const notificationsEnabled = alerts.enabled;
  // The ring itself moved to AdminNavbar (one engine on every page, driven by
  // the server's newOrderAlert). This view keeps the toggle and the status.

  useEffect(() => {
    const token = getTokenFromLocalStorage();
    // Pass auth token so socket.data.user is populated server-side,
    // which is required for the joinSuperadminRoom role guard to pass.
    const socket = io(process.env.REACT_APP_SERVER_URL, {
      auth: token ? { token } : undefined,
    });
    socketRef.current = socket;

    // (Re)tell the server which country's dashboard this socket should receive.
    // Emitting joinDashboard again with a new ISO-2 makes the server leave the
    // old country room and join the new one (see server.js joinDashboard), so a
    // super-admin's country switch immediately re-scopes the live cards without
    // a socket reconnect. Guarded so we don't spam identical re-joins.
    const rejoinDashboard = () => {
      const active = getActiveCountry();
      if (active === joinedCountryRef.current) return;
      joinedCountryRef.current = active;
      setIsLoading(true); // show "processing…" until the new country's stats arrive
      socket.emit("joinDashboard", agentId, active);
    };

    // 30-second fallback: if the superadmin room push hasn't arrived yet,
    // stop the infinite spinner and show a recoverable error message.
    let realtimeTimeoutId = null;
    if (isSuperAdmin) {
      realtimeTimeoutId = setTimeout(() => {
        setRealtimeLoading((prev) => {
          if (prev) {
            setRealtimeError("Live data timed out. The server may still be warming up — try refreshing.");
            return false;
          }
          return prev;
        });
      }, 30_000);
    }

    socket.on("connect", () => {
      console.log("Connected to server");
      setSocketConnected(true);
      // Force a (re)join on (re)connect: reset the cached country so a
      // reconnect after a country switch always re-scopes to the current one.
      joinedCountryRef.current = null;
      rejoinDashboard();
      if (isSuperAdmin) socket.emit("joinSuperadminRoom");
    });

    // A super-admin country switch persists localStorage.activeCountry. In THIS
    // tab that fires our custom `activeCountryChanged` event; OTHER tabs get the
    // native `storage` event. Either way, re-scope the live dashboard socket.
    const onCountryChange = () => {
      if (socket.connected) rejoinDashboard();
    };
    const onStorage = (e) => {
      if (e.key === "activeCountry") onCountryChange();
    };
    window.addEventListener("activeCountryChanged", onCountryChange);
    window.addEventListener("storage", onStorage);

    socket.on("disconnect", () => {
      console.log("Disconnected from server");
      setSocketConnected(false);
    });

    socket.on("dashboardUpdate", (newStats) => {
      setStats(newStats);
      setIsLoading(false);
    });

    // The new-order sound is rung by AdminNavbar on the same server event
    // (`newOrderAlert`), on every page. Nothing to do here.

    if (isSuperAdmin) {
      socket.on("analytics:realtime", (data) => {
        if (realtimeTimeoutId) clearTimeout(realtimeTimeoutId);
        setRealtimeData(data);
        setRealtimeLastUpdated(new Date());
        setRealtimeLoading(false);
        setRealtimeError(null);
      });

      // Server-side GA4 quota backoff: the push loop pauses (up to 1h) when GA4
      // throttles it. Surface that honestly instead of a bogus timeout error.
      socket.on("analytics:realtime:status", (status) => {
        const backoffUntil = status?.backoffUntil || 0;
        if (backoffUntil > Date.now()) {
          const until = new Date(backoffUntil).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          setRealtimeError(`GA4 realtime quota reached — live updates paused until about ${until}. They resume automatically.`);
          setRealtimeLoading(false);
        }
      });

      // Surface server-side auth rejection so admin sees a clear message
      socket.on("error", (err) => {
        if (err?.message?.toLowerCase().includes("admin")) {
          setRealtimeError("Admin access required — your session may have expired. Please log out and back in.");
          setRealtimeLoading(false);
        }
      });
    }

    return () => {
      if (realtimeTimeoutId) clearTimeout(realtimeTimeoutId);
      window.removeEventListener("activeCountryChanged", onCountryChange);
      window.removeEventListener("storage", onStorage);
      if (isSuperAdmin) socket.emit("leaveSuperadminRoom");
      socket.disconnect();
      socketRef.current = null;
      joinedCountryRef.current = null;
    };
  }, [agentId, isSuperAdmin]);

  // HTTP fallback + instant first paint for the stat cards. The dashboard used to
  // depend SOLELY on the live socket `dashboardUpdate`; if that was delayed or lost
  // (e.g. a server restart) every card hung on "processing…" forever. Now we also
  // fetch once over HTTP on mount and on a country switch, and let the socket keep
  // the cards live afterwards.
  useEffect(() => {
    let alive = true;
    const fetchStats = () => {
      apiGetDashboardStats()
        .then((res) => { if (alive && res?.data) { setStats(res.data); setIsLoading(false); } })
        .catch(() => { /* socket may still deliver */ });
    };
    fetchStats();
    const onCountry = () => fetchStats();
    const onStorage = (e) => { if (e.key === "activeCountry") fetchStats(); };
    window.addEventListener("activeCountryChanged", onCountry);
    window.addEventListener("storage", onStorage);
    return () => {
      alive = false;
      window.removeEventListener("activeCountryChanged", onCountry);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Enable/disable now flows through the shared alert engine (unlocks audio,
  // requests notification permission, and registers this browser for Web Push).
  const handleNotificationToggle = alerts.toggle;

  const history = useHistory();

  // Every stat card is a link to the orders list, opened on the filter the
  // card's count was made with (utilities/orderDeepLinks.js): Pending opens
  // the DISPATCHED orders, COD the COD ones, and so on. The whole card is the
  // target, keyboard included, not only the footer line.
  const ordersAudience = isRetailer ? "retailer" : isRestaurant ? "restaurant" : isHealthcare ? "healthcare" : "admin";
  const openOrders = (card, label) => cardLinkProps(() => history.push(cardLink(ordersAudience, card)), label);

  // Ticket sales (staff). The card opens the paid ticket orders from the
  // same moment the server counted from; its warning line opens the ones
  // that need a person. A link inside a link, so the line stops the click
  // reaching the card.
  const ticketSalesCard = { ...CARD_FILTERS.ticketSales, ...(stats.ticketSalesSince ? { from: stats.ticketSalesSince } : {}) };
  const ticketAttention = Number(stats.ticketAttentionCount || 0);
  const openTicketAttention = cardLinkProps(
    () => history.push(cardLink("admin", "ticketAttention")),
    "Open ticket orders that need attention"
  );
  const ticketAttentionProps = {
    ...openTicketAttention,
    onClick: (e) => {
      e.stopPropagation();
      openTicketAttention.onClick(e);
    },
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") e.stopPropagation();
      openTicketAttention.onKeyDown(e);
    },
  };

  // ── Fetch GA4 analytics (super admin only) ────────────────────────────────
  useEffect(() => {
    if (!isSuperAdmin) return;
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    apiGetAnalyticsOverview(analyticsDays)
      .then((res) => setAnalyticsData(res.data))
      .catch((err) => setAnalyticsError(err.response?.data?.message || 'Failed to load analytics'))
      .finally(() => setAnalyticsLoading(false));
  }, [isSuperAdmin, analyticsDays]);

  // Realtime analytics data is pushed from the server via Socket.IO (analytics:realtime event)
  // The socket useEffect above handles joining superadminRoom and receiving the push.
  //
  // HTTP hydration: the socket push only fires every 2 minutes, so waiting for it
  // alone lost a 30s-timer-vs-120s-cadence race on ~75% of page loads ("Live data
  // timed out…"). Fetch once over HTTP for an instant first paint (and for a real
  // Retry), and let the socket keep the panel live afterwards.
  const fetchRealtimeNow = useCallback(() => {
    if (!isSuperAdmin) return;
    setRealtimeLoading(true);
    setRealtimeError(null);
    apiGetAnalyticsRealtime()
      .then((res) => {
        if (res?.data?.success) {
          setRealtimeData(res.data);
          setRealtimeLastUpdated(new Date());
          setRealtimeError(null);
        }
        setRealtimeLoading(false);
      })
      .catch((err) => {
        // Show a real, retryable error — the next socket push (if any) clears it.
        setRealtimeError(err.response?.data?.message || "Couldn't load live analytics — the server may be busy. Tap Retry.");
        setRealtimeLoading(false);
      });
  }, [isSuperAdmin]);

  useEffect(() => { fetchRealtimeNow(); }, [fetchRealtimeNow]);

  // ── Resolve the correct vendor shop path from the server ─────────────────
  useEffect(() => {
    if (!isVendor || isSuperAdmin || isSupervisor) return;
    apiGetVendorShopPath()
      .then((res) => setVendorShopPath(res.data.path))
      .catch(() => {}); // silently ignore — analytics panel will stay hidden
  }, [isVendor, isSuperAdmin, isSupervisor]);

  // ── Fetch vendor-scoped analytics ─────────────────────────────────────────
  useEffect(() => {
    if (!isVendor || !vendorShopPath || isSuperAdmin || isSupervisor) return;
    setVendorAnalyticsLoading(true);
    setVendorAnalyticsError(null);
    apiGetVendorAnalytics(vendorShopPath, vendorAnalyticsDays)
      .then((res) => setVendorAnalyticsData(res.data))
      .catch((err) => setVendorAnalyticsError(err.response?.data?.message || 'Failed to load shop analytics'))
      .finally(() => setVendorAnalyticsLoading(false));
  }, [isVendor, vendorShopPath, vendorAnalyticsDays]);

  return (
    <Container fluid>
      {/* Welcome Header */}
      <Row className="mb-4">
        <Col xs="12">
          <div
            style={{
              background:
                "linear-gradient(135deg,rgb(5, 14, 57) 0%,rgb(19, 98, 116) 100%)",
              borderRadius: "15px",
              padding: "25px 30px",
              color: "white",
              boxShadow: "0 8px 30px rgba(102, 126, 234, 0.25)",
              marginTop: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
          <h2
              style={{
                fontWeight: "700",
                marginBottom: "5px",
                fontSize: "1.8rem",
              }}
            >
              Welcome back, {user?.result?.name || "Admin"}!
            </h2>
            <p style={{ margin: 0, opacity: 0.9, fontSize: "1rem", display: "flex", alignItems: "center", gap: "10px" }}>
              Here's what's happening with your orders today
              {socketConnected && (
                <Badge
                  bg=""
                  style={{
                    fontSize: '10px',
                    padding: '4px 8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    backgroundColor: '#03ab06d1'
                  }}
                >
                  <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: '#ffffffff',
                    animation: 'pulse 1.5s infinite'
                  }} />
                  Live Updates
                </Badge>
              )}
            </p>
            </div>
            {(isSuperAdmin || isSupervisor || isVendor) && (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("openInHouseOrderModal"))}
                style={{ padding: "10px 20px", borderRadius: 8, background: "#f2c811", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer", color: "#0f172a", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", boxShadow: "0 2px 12px rgba(242,200,17,0.4)", alignSelf: "center" }}
              >
                + New In-House Order
              </button>
            )}
          </div>


          </div>
        </Col>
      </Row>
      <Row>
        <Col lg="3" sm="6">
          <Card
            className="card-stats"
            style={{
              borderRadius: "15px",
              overflow: "hidden",
              transition: "all 0.3s ease",
              cursor: "pointer",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              border: "none",
              marginBottom: "20px",
            }}
            {...openOrders("new", "Open new orders")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px)";
              e.currentTarget.style.boxShadow =
                "0 12px 30px rgba(245, 0, 118, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
            }}
          >
            <Card.Body
              style={{
                backgroundColor: "#F50076",
                background: "linear-gradient(135deg, #F50076 0%, #d4006a 100%)",
              }}
            >
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="nc-icon nc-chart text-warning"></i>
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p
                      className="card-category"
                      style={{
                        color: "white",
                        textTransform: "uppercase",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        letterSpacing: "1px",
                        marginBottom: "8px",
                      }}
                    >
                      <i className="fas fa-box-open mr-1"></i> New Order(s)
                    </p>
                    {isLoading ? (
                      <p
                        className="card-title"
                        style={{ color: "white", fontSize: "14px" }}
                      >
                        processing...
                      </p>
                    ) : (
                      <Card.Title
                        as="h4"
                        style={{
                          fontWeight: 600,
                          fontStyle: "italic",
                          fontSize: 26,
                          color: "white",
                        }}
                      >
                        {stats.processingCount}
                      </Card.Title>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer>
              <hr></hr>
              <div className="stats">
                <i className="fas fa-redo mr-1"></i>
                Manage Order(s)
              </div>
            </Card.Footer>
          </Card>

          {/* Ticket sales. A ticket order has no delivery leg, so it never
              counts as a new order above; without this card a sale could go
              unseen (16 Oct 2026). Staff only: the counts are platform-wide. */}
          {isLusukuStaff && (
            <Card
              className="card-stats"
              style={{
                borderRadius: "15px",
                overflow: "hidden",
                transition: "all 0.3s ease",
                cursor: "pointer",
                boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                border: "none",
                marginBottom: "20px",
              }}
              {...openOrders(ticketSalesCard, "Open paid ticket orders from the last 24 hours")}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(15, 118, 110, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
              }}
            >
              <Card.Body
                style={{
                  backgroundColor: "#0f766e",
                  background: "linear-gradient(135deg, #0f766e 0%, #115e59 100%)",
                }}
              >
                <Row>
                  <Col xs="5">
                    <div className="icon-big text-center icon-warning">
                      <i className="fas fa-ticket-alt" style={{ color: "#99f6e4" }}></i>
                    </div>
                  </Col>
                  <Col xs="7">
                    <div className="numbers">
                      <p className="card-category" style={{ color: "white", textTransform: "uppercase" }}>
                        Ticket Sales
                      </p>
                      {isLoading ? (
                        <p className="card-title" style={{ color: "white", fontSize: "14px" }}>
                          processing...
                        </p>
                      ) : (
                        <Card.Title as="h4" style={{ fontWeight: 600, fontStyle: "italic", fontSize: 22, color: "white" }}>
                          {Number(stats.ticketSalesCount || 0)}
                        </Card.Title>
                      )}
                      <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.72rem", margin: 0 }}>Last 24 hours</p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer>
                <hr></hr>
                {ticketAttention > 0 ? (
                  <div className="stats" style={{ color: "#b91c1c", fontWeight: 700 }} {...ticketAttentionProps}>
                    <i className="fas fa-exclamation-circle mr-1"></i>
                    {ticketAttention} {ticketAttention === 1 ? "needs" : "need"} attention
                  </div>
                ) : (
                  <div className="stats">
                    <i className="fas fa-check-circle mr-1"></i>
                    Every paid order has its tickets
                  </div>
                )}
              </Card.Footer>
            </Card>
          )}
        </Col>

        <Col lg="3" sm="6">
          <Card
            className="card-stats"
            style={{
              borderRadius: "15px",
              overflow: "hidden",
              transition: "all 0.3s ease",
              cursor: "pointer",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              border: "none",
              marginBottom: "20px",
            }}
            {...openOrders("pending", "Open orders pending delivery")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px)";
              e.currentTarget.style.boxShadow =
                "0 12px 30px rgba(15, 53, 61, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
            }}
          >
            <Card.Body
              style={{
                backgroundColor: "#0F353D",
                background: "linear-gradient(135deg, #0F353D 0%, #0a252b 100%)",
              }}
            >
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="nc-icon nc-refresh-02 text-success"></i>
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p
                      className="card-category"
                      style={{
                        color: "white",
                        textTransform: "uppercase",
                      }}
                    >
                      Pending Delivery!
                    </p>
                    {isLoading ? (
                      <p
                        className="card-title"
                        style={{ color: "white", fontSize: "14px" }}
                      >
                        processing...
                      </p>
                    ) : (
                      <Card.Title
                        as="h4"
                        style={{
                          fontWeight: 400,
                          fontStyle: "italic",
                          fontSize: 26,
                          color: "white",
                        }}
                      >
                        {stats.dispatchedCount}
                      </Card.Title>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer>
              <hr></hr>
              <div className="stats">
                <i className="far fa-calendar-alt mr-1"></i>
                Dispatched.
              </div>
            </Card.Footer>
          </Card>
          <Card
            className="card-stats"
            style={{
              borderRadius: "15px",
              overflow: "hidden",
              transition: "all 0.3s ease",
              cursor: "pointer",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              border: "none",
              marginTop: "10px",
            }}
            {...openOrders("assigned", "Open assigned orders")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-5px)";
              e.currentTarget.style.boxShadow =
                "0 8px 25px rgba(99, 74, 0, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
            }}
          >
            <Card.Body
              style={{
                backgroundColor: "#634A00",
                background: "linear-gradient(135deg, #634A00 0%, #4a3600 100%)",
              }}
            >
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="fas fa-shipping-fast text-warning"></i>
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p
                      className="card-category"
                      style={{ color: "white", textTransform: "uppercase" }}
                    >
                      Assigned Orders
                    </p>
                    {isLoading ? (
                      <p
                        className="card-title"
                        style={{ color: "white", fontSize: "14px" }}
                      >
                        processing...
                      </p>
                    ) : (
                      <Card.Title
                        as="h4"
                        style={{
                          fontWeight: 600,
                          fontStyle: "italic",
                          fontSize: 18,
                          color: "white",
                        }}
                      >
                        {stats.assignedCount}
                      </Card.Title>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg="3" sm="6">
          <Card
            className="card-stats"
            style={{
              borderRadius: "15px",
              overflow: "hidden",
              transition: "all 0.3s ease",
              cursor: "pointer",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              border: "none",
              marginBottom: "20px",
            }}
            {...openOrders("delivered", "Open delivered orders")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px)";
              e.currentTarget.style.boxShadow =
                "0 12px 30px rgba(12, 125, 116, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
            }}
          >
            <Card.Body
              style={{
                backgroundColor: "#0C7D74",
                background: "linear-gradient(135deg, #0C7D74 0%, #095e57 100%)",
              }}
            >
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="nc-icon nc-delivery-fast text-success"></i>
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p
                      className="card-category"
                      style={{ color: "white", textTransform: "uppercase" }}
                    >
                      Delivered
                    </p>
                    {isLoading ? (
                      <p
                        className="card-title"
                        style={{ color: "white", fontSize: "14px" }}
                      >
                        processing...
                      </p>
                    ) : (
                      <Card.Title
                        as="h4"
                        style={{
                          fontWeight: 600,
                          fontStyle: "italic",
                          fontSize: 26,
                          color: "white",
                        }}
                      >
                        {stats.deliveredCount}
                      </Card.Title>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer>
              <hr></hr>
              <div className="stats">
                <i className="far fa-check-circle mr-1"></i>
                Overall Delivered.
              </div>
            </Card.Footer>
          </Card>
          <Card
            className="card-stats"
            style={{
              borderRadius: "15px",
              overflow: "hidden",
              transition: "all 0.3s ease",
              cursor: "pointer",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              border: "none",
              marginTop: "10px",
            }}
            {...openOrders("delivered", "Open delivered orders, which the average is made from")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-5px)";
              e.currentTarget.style.boxShadow =
                "0 8px 25px rgba(70, 89, 115, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
            }}
          >
            <Card.Body
              style={{
                backgroundColor: "#465973",
                background: "linear-gradient(135deg, #465973 0%, #344358 100%)",
              }}
            >
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="nc-icon nc-time-alarm text-warning"></i>
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p
                      className="card-category"
                      style={{ color: "white", textTransform: "uppercase" }}
                    >
                      Avg Delivery Time
                    </p>
                    <Card.Title
                      as="h4"
                      style={{
                        fontWeight: 300,
                        fontStyle: "italic",
                        fontSize: 16,
                        color: "white",
                      }}
                    >
                      {isLoading ? "processing..." : formatAvgDelivery(stats.avgDeliveryMinutes)}
                    </Card.Title>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg="3" sm="6">
          <Card
            className="card-stats"
            style={{
              borderRadius: "15px",
              overflow: "hidden",
              transition: "all 0.3s ease",
              cursor: "pointer",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              border: "none",
              marginBottom: "20px",
            }}
            {...openOrders("cod", "Open cash on delivery orders")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px)";
              e.currentTarget.style.boxShadow =
                "0 12px 30px rgba(28, 14, 46, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
            }}
          >
            <Card.Body
              style={{
                backgroundColor: "#1C0E2E",
                background: "linear-gradient(135deg, #1C0E2E 0%, #140a22 100%)",
              }}
            >
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="fas fa-hand-holding-usd text-primary"></i>
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">COD</p>
                    {isLoading ? (
                      <p
                        className="card-title"
                        style={{ color: "white", fontSize: "14px" }}
                      >
                        processing...
                      </p>
                    ) : (
                      <Card.Title
                        as="h4"
                        style={{
                          fontWeight: 300,
                          fontStyle: "italic",
                          fontSize: 26,
                          color: "white",
                        }}
                      >
                        +{stats.codCount}
                      </Card.Title>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Card
            className="card-stats"
            style={{
              borderRadius: "15px",
              overflow: "hidden",
              transition: "all 0.3s ease",
              cursor: "pointer",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              border: "none",
              marginTop: "10px",
            }}
            {...openOrders("momo", "Open mobile money orders")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-5px)";
              e.currentTarget.style.boxShadow =
                "0 8px 25px rgba(255, 174, 0, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
            }}
          >
            <Card.Body
              style={{
                backgroundColor: "#FFAE00",
                background: "linear-gradient(135deg, #FFAE00 0%, #e09800 100%)",
              }}
            >
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="nc-icon nc-bank text-primary"></i>
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p
                      className="card-category"
                      style={{
                        color: "#0F353D",
                      }}
                    >
                      MoMo
                    </p>
                    {isLoading ? (
                      <p className="card-title" style={{ fontSize: "14px" }}>
                        processing...
                      </p>
                    ) : (
                      <Card.Title
                        as="h4"
                        style={{
                          fontWeight: 300,
                          fontStyle: "italic",
                          fontSize: 26,
                          color: "white",
                        }}
                      >
                        +{stats.momoCount}
                      </Card.Title>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Card.Footer>
            <hr></hr>
            <div
              className="stats"
              style={{
                cursor: "pointer",
              }}
              onClick={() => history.push(cardLink(ordersAudience, null))}
            >
              <i className="fas fa-redo mr-1"></i>
              Payment Mode
            </div>
          </Card.Footer>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col xs="12">
          <Card
            style={{
              borderRadius: "15px",
              border: "none",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              background:
                !notificationsEnabled
                  ? "linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)"
                  : "linear-gradient(135deg,rgb(13, 146, 35) 0%, #37b24d 100%)",
              padding: "20px",
              transition: "all 0.3s ease",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "15px" }}
              >
                <i
                  className="nc-icon nc-bell-55"
                  style={{
                    fontSize: "50px",
                    color: "white",
                    animation:
                      notificationsEnabled
                        ? "ring 2s infinite"
                        : "none",
                  }}
                ></i>
                <div>
                  <h4 style={{ color: "white", margin: 0, fontWeight: "600", display: "flex", alignItems: "center", gap: "10px" }}>
                    <i className={!notificationsEnabled ? "fas fa-bell-slash" : "fas fa-bell"} style={{ color: 'white' }}></i>
                    {!notificationsEnabled ? "Notifications Off" : "Notifications On"}
                  </h4>
                  <p
                    style={{
                      color: "white",
                      margin: 0,
                      opacity: 0.9,
                      fontSize: "0.9rem",
                    }}
                  >
                    {!notificationsEnabled
                      ? "Click to enable sound alerts for new orders"
                      : "You'll be notified when new orders arrive"}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleNotificationToggle}
                style={{
                  backgroundColor: "white",
                  color: !notificationsEnabled ? "#ff6b6b" : "green",
                  border: "none",
                  padding: "12px 30px",
                  borderRadius: "8px",
                  fontWeight: "600",
                  fontSize: "1rem",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  transition: "all 0.3s ease",
                  marginTop: "10px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                {!notificationsEnabled
                  ? "Enable Notifications"
                  : "Disable Notifications"}
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Revenue and settlement (staff / super-admin), completed-order basis ─
           Sales 360 below answers "how much are we selling". This answers "how
           much of it is ours", including the payment-rail cost that no earlier
           report showed. */}
      {(isSuperAdmin || isSupervisor) && (
        <Row className="mt-3">
          <Col xs="12">
            <PlatformMoneyFlow />
          </Col>
        </Row>
      )}

      {/* ── Platform Sales 360 (staff / super-admin, cross-vendor) ────────── */}
      {(isSuperAdmin || isSupervisor) && (
        <Row className="mt-3">
          <Col xs="12">
            <PlatformSalesAnalytics />
          </Col>
        </Row>
      )}

      {/* ── Vendor / Role Context Panel ─────────────────────────────────── */}
      {!isSuperAdmin && (isVendor || isSupervisor || is3PLPartner || is3PLStaff || isBrandOwner) && (
        <Row className="mt-3">
          <Col xs="12">
            {(() => {
              const panels = {
                restaurant: {
                  label: 'Restaurant Vendor',
                  icon: 'fas fa-utensils',
                  accent: '#e67e22',
                  links: [
                    { label: 'My Menu',       icon: 'fas fa-clipboard-list', path: '/admin/rest-menu' },
                    { label: 'My Orders',     icon: 'fas fa-receipt',        path: '/admin/resOrdersTable' },
                    { label: 'My Riders',     icon: 'fas fa-motorcycle',     path: '/admin/my-riders' },
                    { label: 'My Profile',    icon: 'fas fa-id-card',        path: '/admin/profile' },
                  ],
                },
                healthcare: {
                  label: 'Healthcare Vendor',
                  icon: 'fas fa-clinic-medical',
                  accent: '#27ae60',
                  links: [
                    { label: 'Products',      icon: 'fas fa-pills',          path: '/admin/healthcare-menu' },
                    { label: 'My Orders',     icon: 'fas fa-receipt',        path: '/admin/healthcare-orders' },
                    { label: 'My Riders',     icon: 'fas fa-motorcycle',     path: '/admin/my-riders' },
                    { label: 'My Profile',    icon: 'fas fa-id-card',        path: '/admin/healthcare-account' },
                  ],
                },
                retailer: {
                  label: 'Retailer / Wholesaler',
                  icon: 'fas fa-shopping-basket',
                  accent: '#8e44ad',
                  links: [
                    { label: 'Catalog',       icon: 'fas fa-boxes',          path: '/admin/catalog' },
                    { label: 'Shop Orders',   icon: 'fas fa-receipt',        path: '/admin/retailerWholesalerOrdersTable' },
                    { label: 'My Riders',     icon: 'fas fa-motorcycle',     path: '/admin/my-riders' },
                    { label: 'My Profile',    icon: 'fas fa-id-card',        path: '/admin/rw-profile' },
                  ],
                },
                supervisor: {
                  label: 'Lusuku Manager',
                  icon: 'fas fa-user-tie',
                  accent: '#2980b9',
                  links: [
                    // /admin/vendorOrdersTable is no longer a route; the manager's list is Manage Orders.
                    { label: 'All Orders',    icon: 'fas fa-list-alt',       path: '/admin/paginatedOrdersTable' },
                    { label: 'Vendors',       icon: 'fas fa-store-alt',      path: '/admin/restaurants' },
                    { label: 'Riders',        icon: 'fas fa-motorcycle',     path: '/admin/riders-table' },
                    { label: 'Users',         icon: 'fas fa-users',          path: '/admin/users' },
                  ],
                },
                '3pl_partner': {
                  label: '3PL Logistics Partner',
                  icon: 'fas fa-truck',
                  accent: '#16a085',
                  links: [
                    { label: 'Orders',        icon: 'fas fa-tasks',          path: '/admin/partner-orders' },
                    { label: 'My Riders',     icon: 'fas fa-motorcycle',     path: '/admin/partner-riders' },
                    { label: 'Profile',       icon: 'fas fa-id-badge',       path: '/admin/partner-profile' },
                  ],
                },
                '3pl_staff': {
                  label: '3PL Staff',
                  icon: 'fas fa-user-hard-hat',
                  accent: '#7f8c8d',
                  links: [
                    { label: 'Orders',        icon: 'fas fa-tasks',          path: '/admin/partner-orders' },
                    { label: 'My Riders',     icon: 'fas fa-motorcycle',     path: '/admin/partner-riders' },
                  ],
                },
                brand_owner: {
                  label: 'Brand Owner',
                  icon: 'fas fa-tag',
                  accent: '#0a578b',
                  links: [
                    { label: 'Orders & Quotes', icon: 'fas fa-file-invoice',  path: '/admin/brand-quotes' },
                    { label: 'Catalog',          icon: 'fas fa-boxes',         path: '/admin/catalog' },
                    { label: 'My Brand',         icon: 'fas fa-id-badge',      path: '/admin/brand-profile' },
                  ],
                },
              };
              const key = isRestaurant ? 'restaurant'
                        : isHealthcare ? 'healthcare'
                        : isRetailer   ? 'retailer'
                        : isSupervisor ? 'supervisor'
                        : is3PLPartner ? '3pl_partner'
                        : is3PLStaff   ? '3pl_staff'
                        : isBrandOwner ? 'brand_owner'
                        : null;
              if (!key) return null;
              const panel = panels[key];
              return (
                <div style={{
                  background: 'linear-gradient(135deg,#0d1117 0%,#161b22 100%)',
                  borderRadius: 14,
                  padding: '18px 22px',
                  border: `1px solid ${panel.accent}33`,
                  boxShadow: `0 4px 20px ${panel.accent}22`,
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 180 }}>
                    <div style={{
                      width: 42, height: 42, borderRadius: 10,
                      background: `${panel.accent}22`, border: `1.5px solid ${panel.accent}66`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <i className={panel.icon} style={{ color: panel.accent, fontSize: '1.1rem' }}></i>
                    </div>
                    <div>
                      <div style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.85rem' }}>{panel.label}</div>
                      <div style={{ color: '#6b7a99', fontSize: '0.72rem' }}>{user?.result?.name}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {panel.links.map((lnk) => (
                      <button key={lnk.path} onClick={() => history.push(lnk.path)} style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        background: 'rgba(255,255,255,0.05)', border: `1px solid rgba(255,255,255,0.08)`,
                        borderRadius: 8, padding: '7px 14px', cursor: 'pointer', color: '#c9d3ec',
                        fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = `${panel.accent}22`; e.currentTarget.style.borderColor = `${panel.accent}66`; e.currentTarget.style.color = panel.accent; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#c9d3ec'; }}
                      >
                        <i className={lnk.icon} style={{ fontSize: '0.8rem' }}></i>
                        {lnk.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </Col>
        </Row>
      )}

      {/* ── Chat Analytics (WhatsApp & app conversations) ─────────────── */}
      {isVendor && !isSuperAdmin && !isSupervisor && (
        <ChatAnalytics vendorId={user?.result?._id} />
      )}

      {/* ── Vendor Shop Analytics Panel ──────────────────────────────────── */}
      {isVendor && vendorShopPath && !isSuperAdmin && !isSupervisor && (
        <>
          {/* Section header */}
          <Row className="mt-4">
            <Col xs="12">
              <Card style={{ borderRadius: 15, border: 'none', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', marginBottom: 0 }}>
                <Card.Body style={{
                  background: isRestaurant ? 'linear-gradient(135deg,#e67e22 0%,#d35400 100%)'
                            : isHealthcare ? 'linear-gradient(135deg,#27ae60 0%,#1e8449 100%)'
                            : 'linear-gradient(135deg,#8e44ad 0%,#6c3483 100%)',
                  padding: '14px 24px'
                }}>
                  <Row className="align-items-center">
                    <Col>
                      <div className="numbers">
                        <p className="card-category" style={{ color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '1px', marginBottom: 2 }}>
                          <i className="fas fa-chart-line mr-1"></i> My Shop Performance · lusuku.ug
                        </p>
                        <Card.Title as="h5" style={{ color: '#fff', fontWeight: 700, marginBottom: 0 }}>
                          {isRestaurant ? 'Restaurant Analytics' : isHealthcare ? 'Healthcare Shop Analytics' : 'Shop Analytics'}
                        </Card.Title>
                      </div>
                    </Col>
                    <Col xs="auto">
                      <div style={{ display: 'flex', gap: 4 }}>
                        {[7, 14, 30].map((d) => (
                          <button key={d} onClick={() => setVendorAnalyticsDays(d)} style={{
                            padding: '5px 13px', borderRadius: 20, border: 'none', cursor: 'pointer',
                            fontSize: '0.72rem', fontWeight: 700, transition: 'all 0.15s',
                            background: vendorAnalyticsDays === d ? '#fff' : 'rgba(255,255,255,0.2)',
                            color: vendorAnalyticsDays === d
                              ? (isRestaurant ? '#e67e22' : isHealthcare ? '#27ae60' : '#8e44ad')
                              : '#fff',
                          }}>{d}d</button>
                        ))}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Loading / Error */}
          {vendorAnalyticsLoading && (
            <Row className="mt-3"><Col className="text-center py-4">
              <Spinner animation="border" size="sm" variant="warning" />
              <p className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>Loading your shop analytics…</p>
            </Col></Row>
          )}
          {vendorAnalyticsError && (
            <Row className="mt-3"><Col>
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 10, padding: '12px 16px', fontSize: '0.85rem', color: '#856404', display: 'flex', alignItems: 'center', gap: 10 }}>
                <i className="fas fa-exclamation-triangle"></i>
                <span>{vendorAnalyticsError}</span>
                <span onClick={() => setVendorAnalyticsDays(d => d)} style={{ marginLeft: 'auto', cursor: 'pointer', textDecoration: 'underline' }}>Retry</span>
              </div>
            </Col></Row>
          )}

          {/* KPI cards — shop-scoped */}
          {vendorAnalyticsData && (() => {
            const s = vendorAnalyticsData.summary;
            const accentColor = isRestaurant ? '#e67e22' : isHealthcare ? '#27ae60' : '#8e44ad';
            const kpis = [
              { label: 'Shop Visitors',  value: Number(s.totalUsers).toLocaleString(),    footer: `Last ${vendorAnalyticsDays} days`,  ncIcon: 'nc-icon nc-single-02',   gradient: isRestaurant ? 'linear-gradient(135deg,#e67e22 0%,#d35400 100%)' : isHealthcare ? 'linear-gradient(135deg,#27ae60 0%,#1e8449 100%)' : 'linear-gradient(135deg,#8e44ad 0%,#6c3483 100%)', glow: isRestaurant ? 'rgba(230,126,34,0.3)' : isHealthcare ? 'rgba(39,174,96,0.3)' : 'rgba(142,68,173,0.3)' },
              { label: 'Page Views',     value: Number(s.pageViews).toLocaleString(),     footer: `${(Number(s.pageViews)/Math.max(Number(s.sessions),1)).toFixed(1)} per session`, ncIcon: 'nc-icon nc-tv-2', gradient: 'linear-gradient(135deg,#1a56db 0%,#1e3a8a 100%)', glow: 'rgba(26,86,219,0.3)' },
              { label: 'Sessions',       value: Number(s.sessions).toLocaleString(),      footer: `${vendorAnalyticsDays}-day window`,  ncIcon: 'nc-icon nc-tap-01',      gradient: 'linear-gradient(135deg,#0694a2 0%,#164e63 100%)', glow: 'rgba(6,148,162,0.3)' },
              { label: 'Bounce Rate',    value: `${(Number(s.bounceRate)*100).toFixed(1)}%`, footer: Number(s.bounceRate) < 0.5 ? 'Good — visitors exploring' : 'High — check your menu', ncIcon: 'nc-icon nc-refresh-02', gradient: Number(s.bounceRate) < 0.5 ? 'linear-gradient(135deg,#057a55 0%,#014737 100%)' : 'linear-gradient(135deg,#c81e1e 0%,#771d1d 100%)', glow: Number(s.bounceRate) < 0.5 ? 'rgba(5,122,85,0.3)' : 'rgba(200,30,30,0.3)' },
              { label: 'Avg Time on Page', value: `${Math.floor(Number(s.avgSessionDuration)/60)}m ${Math.round(Number(s.avgSessionDuration)%60)}s`, footer: 'per visit', ncIcon: 'nc-icon nc-time-alarm', gradient: 'linear-gradient(135deg,#7e3af2 0%,#5521b5 100%)', glow: 'rgba(126,58,242,0.3)' },
            ];
            return (
              <>
                <Row className="mt-3">
                  {kpis.map(({ label, value, footer, ncIcon, gradient, glow }) => (
                    <Col xs="6" sm="4" lg key={label} className="mb-3">
                      <Card className="card-stats" style={{ borderRadius: 15, border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', overflow: 'hidden', transition: 'all 0.3s', marginBottom: 0 }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = `0 12px 28px ${glow}`; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)'; }}
                      >
                        <Card.Body style={{ background: gradient }}>
                          <Row>
                            <Col xs="4">
                              <div className="icon-big text-center icon-warning">
                                <i className={ncIcon} style={{ color: 'rgba(255,255,255,0.85)' }}></i>
                              </div>
                            </Col>
                            <Col xs="8">
                              <div className="numbers">
                                <p className="card-category" style={{ color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.8px' }}>{label}</p>
                                <Card.Title as="h4" style={{ color: '#fff', fontWeight: 700, fontStyle: 'italic', fontSize: 22 }}>{value}</Card.Title>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                        <Card.Footer>
                          <hr />
                          <div className="stats">
                            <i className="fas fa-store mr-1" style={{ color: accentColor }}></i>
                            {footer}
                          </div>
                        </Card.Footer>
                      </Card>
                    </Col>
                  ))}
                </Row>

                {/* Top pages + devices row */}
                <Row className="mt-1">
                  {/* Top sub-pages */}
                  <Col xs="12" md="7" className="mb-4">
                    <Card style={{ borderRadius: 15, border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', height: '100%' }}>
                      <Card.Header style={{ background: 'transparent', borderBottom: '1px solid #f1f1f1', paddingBottom: 12 }}>
                        <Card.Title as="h5" style={{ fontWeight: 700, marginBottom: 2 }}>Top Shop Pages</Card.Title>
                        <p className="card-category" style={{ fontSize: '0.78rem', marginBottom: 0 }}>
                          Most viewed pages inside your shop
                        </p>
                      </Card.Header>
                      <Card.Body style={{ paddingTop: 12 }}>
                        {(vendorAnalyticsData.topPages || []).length === 0 ? (
                          <p className="card-category text-center py-3">No page view data yet for this shop.</p>
                        ) : (() => {
                          const pages = vendorAnalyticsData.topPages.slice(0, 5);
                          const maxV = Math.max(...pages.map(p => Number(p.screenPageViews)), 1);
                          return pages.map((page, i) => {
                            const clr = [accentColor, '#1a56db', '#0694a2', '#7e3af2', '#057a55'][i] || accentColor;
                            return (
                              <div key={i} style={{ marginBottom: 14 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                  <span style={{ fontSize: '0.78rem', color: '#525f7f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '72%' }} title={page.pagePath}>
                                    <i className="fas fa-file-alt mr-1" style={{ color: clr, fontSize: '0.7rem' }}></i>
                                    {page.pagePath || '/'}
                                  </span>
                                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: clr, whiteSpace: 'nowrap' }}>{Number(page.screenPageViews).toLocaleString()}</span>
                                </div>
                                <div style={{ background: '#f1f3f9', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                                  <div style={{ width: `${(Number(page.screenPageViews)/maxV)*100}%`, height: '100%', background: clr, borderRadius: 4, transition: 'width 0.6s ease' }} />
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </Card.Body>
                      <Card.Footer>
                        <hr />
                        <div className="stats">
                          <i className="fas fa-history mr-1"></i>
                          Last {vendorAnalyticsDays} days
                        </div>
                      </Card.Footer>
                    </Card>
                  </Col>

                  {/* Device split */}
                  <Col xs="12" md="5" className="mb-4">
                    <Card style={{ borderRadius: 15, border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', height: '100%' }}>
                      <Card.Header style={{ background: 'transparent', borderBottom: '1px solid #f1f1f1', paddingBottom: 12 }}>
                        <Card.Title as="h5" style={{ fontWeight: 700, marginBottom: 2 }}>
                          <i className="fas fa-mobile-alt mr-2" style={{ color: accentColor }}></i>How Customers Browse
                        </Card.Title>
                        <p className="card-category" style={{ fontSize: '0.78rem', marginBottom: 0 }}>Device types visiting your shop</p>
                      </Card.Header>
                      <Card.Body style={{ paddingTop: 12 }}>
                        {(vendorAnalyticsData.devices || []).length === 0 ? (
                          <p className="card-category text-center py-3">No device data yet.</p>
                        ) : (() => {
                          const devices = vendorAnalyticsData.devices;
                          const total = devices.reduce((s, x) => s + Number(x.sessions), 0) || 1;
                          const devColors = { desktop: '#1a56db', mobile: accentColor, tablet: '#057a55' };
                          const devIcons  = { desktop: 'fas fa-desktop', mobile: 'fas fa-mobile-alt', tablet: 'fas fa-tablet-alt' };
                          return devices.map((d, i) => {
                            const pct = (Number(d.sessions) / total) * 100;
                            const clr = devColors[d.deviceCategory] || ['#7e3af2','#0694a2','#c81e1e'][i] || '#adb5bd';
                            const ico = devIcons[d.deviceCategory] || 'fas fa-question';
                            return (
                              <div key={i} style={{ marginBottom: 16 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                                  <span style={{ fontSize: '0.8rem', color: '#525f7f', textTransform: 'capitalize', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <i className={ico} style={{ color: clr, width: 14, textAlign: 'center' }}></i>{d.deviceCategory}
                                  </span>
                                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: clr }}>{pct.toFixed(1)}%</span>
                                </div>
                                <div style={{ background: '#f1f3f9', borderRadius: 20, height: 10, overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: clr, borderRadius: 20, transition: 'width 0.6s ease' }} />
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </Card.Body>
                      <Card.Footer>
                        <hr />
                        <div className="stats">
                          <i className="fas fa-info-circle mr-1"></i>
                          {Number(vendorAnalyticsData.summary.totalUsers).toLocaleString()} unique visitors
                        </div>
                      </Card.Footer>
                    </Card>
                  </Col>
                </Row>
              </>
            );
          })()}

          {!vendorAnalyticsLoading && !vendorAnalyticsError && !vendorAnalyticsData && (
            <Row className="mt-3"><Col xs="12">
              <Card style={{ borderRadius: 15, border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.08)' }}>
                <Card.Body className="text-center py-5">
                  <i className="fas fa-satellite-dish" style={{ fontSize: '2.5rem', color: '#adb5bd', display: 'block', marginBottom: 12 }}></i>
                  <p className="card-category mb-0">No shop data yet — your shop page needs visitors to appear here.</p>
                </Card.Body>
              </Card>
            </Col></Row>
          )}
        </>
      )}

      {/* ── GA4 Analytics Panel — Power BI Style (Super Admin only) ───────── */}
      {isSuperAdmin && (
        <Row className="mt-4">
          <Col xs="12">
            <div style={{ background: '#1a1f2e', borderRadius: 16, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.35)' }}>

                      {/* ── Toolbar ── */}
              <div style={{
                background: '#141824', padding: '14px 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
                borderBottom: '1px solid rgba(255,255,255,0.06)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: 'linear-gradient(135deg,#e3a008,#f59e0b)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}><i className="fas fa-chart-line" style={{ color: '#141824', fontSize: '0.95rem' }}></i></div>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.92rem', letterSpacing: '0.3px' }}><i className="fab fa-google mr-2" style={{ color: '#f59e0b' }}></i>Website Analytics</div>
                    <div style={{ color: '#6b7a99', fontSize: '0.72rem' }}>Google Analytics 4 · {(analyticsData?.appSummary || (realtimeData?.platforms && realtimeData.platforms.app !== null && realtimeData.platforms.app !== undefined)) ? 'lusuku.ug + mobile app' : 'lusuku.ug'} · {analyticsTab === 'realtime' ? 'Live data · server push every 2 min' : `Last ${analyticsDays} days`}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Tab switcher */}
                  <div style={{ display: 'flex', gap: 2, background: '#1a1f2e', borderRadius: 8, padding: 3 }}>
                    <button onClick={() => setAnalyticsTab('overview')} style={{
                      padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                      fontSize: '0.75rem', fontWeight: 700, transition: 'all 0.15s',
                      background: analyticsTab === 'overview' ? 'linear-gradient(135deg,#e3a008,#f59e0b)' : 'transparent',
                      color: analyticsTab === 'overview' ? '#141824' : '#6b7a99',
                    }}><i className="fas fa-chart-bar mr-1"></i>Overview</button>
                    <button onClick={() => setAnalyticsTab('realtime')} style={{
                      padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                      fontSize: '0.75rem', fontWeight: 700, transition: 'all 0.15s',
                      background: analyticsTab === 'realtime' ? 'linear-gradient(135deg,#10b981,#059669)' : 'transparent',
                      color: analyticsTab === 'realtime' ? '#fff' : '#6b7a99',
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}>
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: analyticsTab === 'realtime' ? '#fff' : '#10b981',
                        animation: 'pulse 1.5s infinite',
                        display: 'inline-block', flexShrink: 0,
                      }} />
                      Realtime
                    </button>
                  </div>
                  {/* Day range — only for overview */}
                  {analyticsTab === 'overview' && (
                    <div style={{ display: 'flex', gap: 4, background: '#1a1f2e', borderRadius: 8, padding: 3 }}>
                      {[7, 14, 30].map((d) => (
                        <button key={d} onClick={() => setAnalyticsDays(d)} style={{
                          padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                          fontSize: '0.75rem', fontWeight: 700, transition: 'all 0.15s',
                          background: analyticsDays === d ? 'linear-gradient(135deg,#e3a008,#f59e0b)' : 'transparent',
                          color: analyticsDays === d ? '#141824' : '#6b7a99',
                        }}>{d}d</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Body ── */}
              <div style={{ padding: '20px' }}>

                {/* ════════════════ OVERVIEW TAB ════════════════ */}
                {analyticsTab === 'overview' && (
                  analyticsLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <Spinner animation="border" size="sm" style={{ color: '#f59e0b' }} />
                      <p style={{ marginTop: 12, color: '#6b7a99', fontSize: '0.82rem' }}>Fetching analytics data…</p>
                    </div>
                  ) : analyticsError ? (
                    <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '14px 18px', color: '#f87171', fontSize: '0.84rem', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <i className="fas fa-exclamation-triangle" style={{ fontSize: '1rem', flexShrink: 0 }}></i>
                      <span>{analyticsError}</span>
                      <span onClick={() => setAnalyticsDays(d => d)} style={{ marginLeft: 'auto', cursor: 'pointer', color: '#f59e0b', textDecoration: 'underline', whiteSpace: 'nowrap' }}>Retry</span>
                    </div>
                  ) : analyticsData ? (
                    <>
                      {/* ── KPI Cards ── */}
                      <Row className="mb-3">
                        {[
                          { label: 'Total Users',  value: Number(analyticsData.summary.totalUsers).toLocaleString(),  sub: `${Number(analyticsData.summary.newUsers).toLocaleString()} new`, color: '#3b82f6', icon: 'fas fa-users',        tip: 'Total unique people who visited lusuku.ug at least once in the selected period. Each person is counted once, no matter how many times they came back.' },
                          { label: 'Sessions',     value: Number(analyticsData.summary.sessions).toLocaleString(),    sub: `${analyticsDays}-day window`,                                   color: '#8b5cf6', icon: 'fas fa-link',          tip: 'A session is a single continuous visit. One person can have many sessions — e.g. visiting in the morning and again at night counts as two sessions.' },
                          { label: 'Page Views',   value: Number(analyticsData.summary.pageViews).toLocaleString(),   sub: `${(Number(analyticsData.summary.pageViews)/Math.max(Number(analyticsData.summary.sessions),1)).toFixed(1)} / session`, color: '#f59e0b', icon: 'fas fa-eye', tip: 'Total number of individual pages loaded across all visits. The number below shows the average pages per visit — higher means users are exploring more of the site.' },
                          { label: 'Bounce Rate',  value: `${(Number(analyticsData.summary.bounceRate)*100).toFixed(1)}%`, sub: Number(analyticsData.summary.bounceRate) < 0.5 ? 'Healthy' : 'High', color: Number(analyticsData.summary.bounceRate) < 0.5 ? '#10b981' : '#ef4444', icon: 'fas fa-sign-out-alt', tip: 'Percentage of visits where the person left after viewing only one page. Under 50% is healthy. A high bounce rate may mean the landing page needs work.' },
                          { label: 'Avg Session',  value: `${Math.floor(Number(analyticsData.summary.avgSessionDuration)/60)}m ${Math.round(Number(analyticsData.summary.avgSessionDuration)%60)}s`, sub: 'per visit', color: '#06b6d4', icon: 'fas fa-stopwatch', tip: 'Average time a visitor spends on the site per visit. Longer times suggest they are browsing menus, reading product details, or placing orders — a positive sign.' },
                        ].map(({ label, value, sub, color, icon, tip }) => (
                          <Col xs="6" sm="4" lg key={label} className="mb-3">
                            <div style={{ background: '#141824', borderRadius: 12, padding: '16px 14px', borderLeft: `3px solid ${color}`, height: '100%' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                <span style={{ fontSize: '0.68rem', color: '#6b7a99', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', display: 'flex', alignItems: 'center' }}>
                                  {label}<InfoTip text={tip} />
                                </span>
                                <i className={icon} style={{ color: color, fontSize: '0.9rem', opacity: 0.8 }}></i>
                              </div>
                              <div style={{ fontSize: '1.55rem', fontWeight: 700, color: '#f0f4ff', lineHeight: 1.1, marginBottom: 4 }}>{value}</div>
                              <div style={{ fontSize: '0.7rem', color: color, fontWeight: 500 }}>{sub}</div>
                            </div>
                          </Col>
                        ))}
                      </Row>

                      {/* ── Mobile app usage (separate GA4 property: lusuku-mobile) ── */}
                      {analyticsData.appSummary && (
                        <div style={{ background: '#141824', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 18 }}>
                          <span style={{ fontSize: '0.68rem', color: '#a78bfa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', display: 'flex', alignItems: 'center' }}>
                            <i className="fas fa-mobile-alt mr-2"></i>Mobile App
                            <InfoTip text="Usage of the Lusuku mobile app (Android/iOS), measured by Firebase Analytics in its own GA4 property. These numbers are separate from the website figures above." />
                          </span>
                          {[
                            { label: 'App Users', value: Number(analyticsData.appSummary.totalUsers).toLocaleString() },
                            { label: 'New', value: Number(analyticsData.appSummary.newUsers).toLocaleString() },
                            { label: 'Sessions', value: Number(analyticsData.appSummary.sessions).toLocaleString() },
                            { label: 'Screen Views', value: Number(analyticsData.appSummary.screenViews).toLocaleString() },
                            { label: 'Avg Session', value: `${Math.floor(Number(analyticsData.appSummary.avgSessionDuration)/60)}m ${Math.round(Number(analyticsData.appSummary.avgSessionDuration)%60)}s` },
                          ].map(({ label, value }) => (
                            <span key={label} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                              <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f0f4ff' }}>{value}</span>
                              <span style={{ fontSize: '0.68rem', color: '#6b7a99' }}>{label}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* ── Charts row ── */}
                      <Row>
                        {/* Top Pages — horizontal bar chart */}
                        <Col xs="12" md="7" className="mb-3">
                          <div style={{ background: '#141824', borderRadius: 12, padding: '16px 18px', height: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                              <span style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'flex', alignItems: 'center' }}>
                                <i className="fas fa-chart-bar mr-2" style={{ color: '#3b82f6' }}></i>Top Pages by Views
                                <InfoTip text="The most visited pages on lusuku.ug, ranked by total number of times they were loaded. Helps you understand which vendor shops or content pages customers visit most." />
                              </span>
                              <span style={{ color: '#3b82f6', fontSize: '0.7rem', fontWeight: 600 }}>lusuku.ug</span>
                            </div>
                            {(() => {
                              const pages = (analyticsData.topPages || []).slice(0, 7);
                              const maxViews = Math.max(...pages.map(p => Number(p.screenPageViews)), 1);
                              return pages.map((page, i) => {
                                const pct = (Number(page.screenPageViews) / maxViews) * 100;
                                const barColors = ['#3b82f6','#8b5cf6','#f59e0b','#10b981','#06b6d4','#f43f5e','#a3e635'];
                                return (
                                  <div key={i} style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                      <span style={{ fontSize: '0.74rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }} title={page.pagePath}>{page.label || page.pagePath || '/'}</span>
                                      <span style={{ fontSize: '0.72rem', color: barColors[i], fontWeight: 700, whiteSpace: 'nowrap', marginLeft: 8 }}>{Number(page.screenPageViews).toLocaleString()}</span>
                                    </div>
                                    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                                      <div style={{ width: `${pct}%`, height: '100%', background: barColors[i], borderRadius: 4, transition: 'width 0.6s ease' }} />
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </Col>

                        {/* Right column: Devices + Countries */}
                        <Col xs="12" md="5" className="mb-3">
                          <div style={{ background: '#141824', borderRadius: 12, padding: '16px 18px', marginBottom: 12 }}>
                            <span style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                              <i className="fas fa-mobile-alt mr-2" style={{ color: '#f59e0b' }}></i>Device Breakdown
                              <InfoTip text="Shows which type of device visitors use to access the site — phone, computer, or tablet. In Uganda, most customers browse on mobile phones, so the site is optimised for small screens." />
                            </span>
                            {(() => {
                              const devices = analyticsData.devices || [];
                              const total = devices.reduce((s, x) => s + Number(x.sessions), 0) || 1;
                              const devColors = { desktop: '#3b82f6', mobile: '#f59e0b', tablet: '#10b981' };
                              return devices.map((d, i) => {
                                const pct = ((Number(d.sessions) / total) * 100);
                                const clr = devColors[d.deviceCategory] || ['#8b5cf6','#06b6d4','#f43f5e'][i] || '#6b7a99';
                                return (
                                  <div key={i} style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                      <span style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'capitalize' }}>{d.deviceCategory}</span>
                                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: clr }}>{pct.toFixed(1)}%</span>
                                    </div>
                                    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                                      <div style={{ width: `${pct}%`, height: '100%', background: clr, borderRadius: 4, transition: 'width 0.6s ease' }} />
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                          <div style={{ background: '#141824', borderRadius: 12, padding: '16px 18px' }}>
                            <span style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                              <i className="fas fa-globe-africa mr-2" style={{ color: '#10b981' }}></i>Top Countries
                              <InfoTip text="Countries that website visitors are browsing from. Uganda should be number one. Significant traffic from other countries (United Kingdom, United States, United Arab Emirates) shows Lusuku is gaining international visibility." />
                            </span>
                            {(() => {
                              const countries = analyticsData.countries || [];
                              const maxUsers = Math.max(...countries.map(c => Number(c.totalUsers)), 1);
                              return countries.map((c, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
                                  <span style={{ fontSize: '0.75rem', color: '#94a3b8', width: 90, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.country}</span>
                                  <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 6 }}>
                                    <div style={{ width: `${(Number(c.totalUsers)/maxUsers)*100}%`, height: '100%', background: '#10b981', borderRadius: 4, transition: 'width 0.6s ease' }} />
                                  </div>
                                  <span style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 700, width: 40, textAlign: 'right', flexShrink: 0 }}>{Number(c.totalUsers).toLocaleString()}</span>
                                </div>
                              ));
                            })()}
                          </div>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <i className="fas fa-satellite-dish" style={{ fontSize: '2.2rem', color: '#6b7a99', marginBottom: 12, display: 'block' }}></i>
                      <p style={{ color: '#6b7a99', fontSize: '0.85rem', margin: 0 }}>No data yet. Deploy lusuku.ug with the GA4 tag to start collecting.</p>
                    </div>
                  )
                )}

                {/* ════════════════ REALTIME TAB ════════════════ */}
                {analyticsTab === 'realtime' && (
                  realtimeLoading && !realtimeData ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <Spinner animation="border" size="sm" style={{ color: '#10b981' }} />
                      <p style={{ marginTop: 12, color: '#6b7a99', fontSize: '0.82rem' }}>Connecting to live data…</p>
                    </div>
                  ) : realtimeError ? (
                    <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '14px 18px', color: '#f87171', fontSize: '0.84rem', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <i className="fas fa-exclamation-triangle" style={{ fontSize: '1rem', flexShrink: 0 }}></i>
                      <span>{realtimeError}</span>
                      <span onClick={fetchRealtimeNow} style={{ marginLeft: 'auto', cursor: 'pointer', color: '#10b981', textDecoration: 'underline', whiteSpace: 'nowrap' }}>Retry</span>
                    </div>
                  ) : realtimeData ? (
                    <>
                      {/* ── Live hero: active users + last updated ── */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          <div style={{
                            background: 'linear-gradient(135deg,#10b981,#059669)',
                            borderRadius: 16, padding: '18px 28px',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120,
                            boxShadow: '0 4px 20px rgba(16,185,129,0.35)',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'pulse 1.5s infinite', display: 'inline-block' }} />
                              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', display: 'flex', alignItems: 'center' }}>Active Now<InfoTip text="People who have been active on lusuku.ug in the last 30 minutes (GA4 realtime window). This number is live and pushed to your screen every 2 minutes via the server." /></span>
                            </div>
                            <div style={{ fontSize: '2.8rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>{realtimeData.activeUsers}</div>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', marginTop: 4 }}>
                              {realtimeData.platforms && realtimeData.platforms.app !== null && realtimeData.platforms.app !== undefined
                                ? `web ${realtimeData.platforms.web} · app ${realtimeData.platforms.app}`
                                : 'users on lusuku.ug'}
                            </div>
                          </div>

                          {/* Device split mini-pills */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {(realtimeData.devices || []).map((d, i) => {
                              const devColors = { desktop: '#3b82f6', mobile: '#f59e0b', tablet: '#10b981' };
                              const devIcons  = { desktop: 'fas fa-desktop', mobile: 'fas fa-mobile-alt', tablet: 'fas fa-tablet-alt' };
                              const clr = devColors[d.deviceCategory] || ['#8b5cf6','#06b6d4','#f43f5e'][i] || '#6b7a99';
                              const ico = devIcons[d.deviceCategory] || 'fas fa-question';
                              return (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#141824', borderRadius: 8, padding: '6px 12px' }}>
                                  <i className={ico} style={{ color: clr, fontSize: '0.78rem', width: 14, textAlign: 'center' }}></i>
                                  <span style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'capitalize', minWidth: 56 }}>{d.deviceCategory}</span>
                                  <span style={{ color: clr, fontWeight: 700, fontSize: '0.82rem' }}>{d.activeUsers}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Refresh indicator */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                          {realtimeLoading && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Spinner animation="border" size="sm" style={{ color: '#10b981', width: 12, height: 12, borderWidth: 2 }} />
                              <span style={{ color: '#6b7a99', fontSize: '0.7rem' }}>Refreshing…</span>
                            </div>
                          )}
                          {realtimeLastUpdated && (
                            <span style={{ color: '#6b7a99', fontSize: '0.7rem' }}>
                              <i className="fas fa-clock mr-1"></i>
                              Updated {realtimeLastUpdated.toLocaleTimeString()}
                            </span>
                          )}
                          <span style={{ color: '#374151', fontSize: '0.65rem' }}>Server pushes updates every 2 min via socket</span>
                        </div>
                      </div>

                      {/* ── Top pages + Events + Countries row ── */}
                      <Row>
                        {/* Top Active Pages */}
                        <Col xs="12" md="5" className="mb-3">
                          <div style={{ background: '#141824', borderRadius: 12, padding: '16px 18px', height: '100%' }}>
                            <span style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'flex', alignItems: 'center', marginBottom: 14 }}>
                              <i className="fas fa-fire mr-2" style={{ color: '#f43f5e' }}></i>Active Pages
                              <InfoTip text="Pages that have at least one person on them right now. The bar width shows each page's share of current visitors relative to the most visited page at this moment." />
                            </span>
                            {(realtimeData.topPages || []).length === 0 ? (
                              <p style={{ color: '#6b7a99', fontSize: '0.8rem', textAlign: 'center', padding: '20px 0' }}>No active pages right now.</p>
                            ) : (() => {
                              const pages = realtimeData.topPages;
                              const maxU = Math.max(...pages.map(p => Number(p.activeUsers)), 1);
                              const barColors = ['#10b981','#3b82f6','#f59e0b','#8b5cf6','#06b6d4','#f43f5e','#a3e635','#ec4899'];
                              return pages.map((page, i) => {
                                const pct = (Number(page.activeUsers) / maxU) * 100;
                                const clr = barColors[i] || '#10b981';
                                // App screens (from the lusuku-mobile property) are tagged so
                                // they're distinguishable from web page titles.
                                const label = (page.platform === 'app' ? 'App · ' : '') + (page.unifiedScreenName || '/');
                                return (
                                  <div key={i} style={{ marginBottom: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                      <span style={{ fontSize: '0.74rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '78%' }} title={label}>{label}</span>
                                      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: clr, whiteSpace: 'nowrap', marginLeft: 6 }}>
                                        {page.activeUsers} <span style={{ fontWeight: 400, color: '#6b7a99' }}>active</span>
                                      </span>
                                    </div>
                                    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                                      <div style={{ width: `${pct}%`, height: '100%', background: clr, borderRadius: 4, transition: 'width 0.8s ease' }} />
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </Col>

                        {/* Live Events */}
                        <Col xs="12" md="4" className="mb-3">
                          <div style={{ background: '#141824', borderRadius: 12, padding: '16px 18px', height: '100%' }}>
                            <span style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'flex', alignItems: 'center', marginBottom: 14 }}>
                              <i className="fas fa-bolt mr-2" style={{ color: '#f59e0b' }}></i>Live Events
                              <span style={{ color: '#6b7a99', fontWeight: 400, fontSize: '0.66rem', marginLeft: 6 }}>last 30 min</span>
                              <InfoTip text="Actions tracked by Google Analytics in the last 30 minutes. page_view = a page was opened. session_start = a new visit began. first_visit = a brand new visitor arrived. scroll = someone scrolled down. purchase = an order was placed." />
                            </span>
                            {(realtimeData.events || []).length === 0 ? (
                              <p style={{ color: '#6b7a99', fontSize: '0.8rem', textAlign: 'center', padding: '20px 0' }}>No events tracked yet.</p>
                            ) : (() => {
                              const events = realtimeData.events;
                              const maxE = Math.max(...events.map(e => Number(e.eventCount)), 1);
                              const evtColors = ['#f59e0b','#3b82f6','#10b981','#8b5cf6','#06b6d4','#f43f5e'];
                              const evtIcons = {
                                page_view: 'fas fa-eye', first_visit: 'fas fa-user-plus',
                                session_start: 'fas fa-play', scroll: 'fas fa-arrow-down',
                                click: 'fas fa-mouse-pointer', purchase: 'fas fa-shopping-cart',
                              };
                              return events.map((evt, i) => {
                                const clr = evtColors[i] || '#6b7a99';
                                const ico = evtIcons[evt.eventName] || 'fas fa-circle';
                                const pct = (Number(evt.eventCount) / maxE) * 100;
                                return (
                                  <div key={i} style={{ marginBottom: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                      <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.76rem', color: '#94a3b8' }}>
                                        <i className={ico} style={{ color: clr, fontSize: '0.72rem', width: 12, textAlign: 'center' }}></i>
                                        {evt.eventName}
                                      </span>
                                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: clr }}>{Number(evt.eventCount).toLocaleString()}</span>
                                    </div>
                                    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 4, overflow: 'hidden' }}>
                                      <div style={{ width: `${pct}%`, height: '100%', background: clr, borderRadius: 4, transition: 'width 0.8s ease' }} />
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </Col>

                        {/* Live Countries */}
                        <Col xs="12" md="3" className="mb-3">
                          <div style={{ background: '#141824', borderRadius: 12, padding: '16px 18px', height: '100%' }}>
                            <span style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'flex', alignItems: 'center', marginBottom: 14 }}>
                              <i className="fas fa-globe-africa mr-2" style={{ color: '#10b981' }}></i>By Country
                              <InfoTip text="Countries that the people currently on lusuku.ug are browsing from. Shows where live traffic is coming from right now." />
                            </span>
                            {(realtimeData.topCountries || []).length === 0 ? (
                              <p style={{ color: '#6b7a99', fontSize: '0.8rem', textAlign: 'center', padding: '20px 0' }}>No location data.</p>
                            ) : (() => {
                              const countries = realtimeData.topCountries;
                              const maxC = Math.max(...countries.map(c => Number(c.activeUsers)), 1);
                              return countries.map((c, i) => (
                                <div key={i} style={{ marginBottom: 10 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: '0.74rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '72%' }}>{c.country}</span>
                                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#10b981' }}>{c.activeUsers}</span>
                                  </div>
                                  <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                                    <div style={{ width: `${(Number(c.activeUsers)/maxC)*100}%`, height: '100%', background: '#10b981', borderRadius: 4, transition: 'width 0.8s ease' }} />
                                  </div>
                                </div>
                              ));
                            })()}
                          </div>
                        </Col>
                      </Row>
                    </>
                  ) : null
                )}

              </div>

            </div>
          </Col>
        </Row>
      )}

      <style>{`
        @keyframes ring {
          0%, 100% { transform: rotate(0deg); }
          10%, 30% { transform: rotate(-15deg); }
          20%, 40% { transform: rotate(15deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes tipFadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(4px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        .infotip-wrap { position: relative; }
        .infotip-bubble {
          display: none;
          position: absolute;
          bottom: calc(100% + 7px);
          left: 50%;
          transform: translateX(-50%);
          background: #0d1117;
          color: #c9d3ec;
          font-size: 0.72rem;
          font-weight: 400;
          line-height: 1.45;
          white-space: normal;
          width: 210px;
          padding: 8px 11px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.1);
          box-shadow: 0 6px 20px rgba(0,0,0,0.5);
          z-index: 9999;
          pointer-events: none;
          animation: tipFadeIn 0.15s ease;
          text-transform: none;
          letter-spacing: 0;
          font-weight: 400;
        }
        .infotip-bubble::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: rgba(255,255,255,0.1);
        }
        .infotip-bubble::before {
          content: '';
          position: absolute;
          top: calc(100% - 1px);
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: #0d1117;
          z-index: 1;
        }
        .infotip-wrap:hover .infotip-bubble { display: block; }
        .infotip-wrap:hover .fa-info-circle { color: #f59e0b !important; }
      `}</style>
    </Container>
  );
};

export default Dashboard;

// import React, { useCallback, useEffect, useRef, useState } from "react";
// import { Button, Card, Container, Row, Col } from "react-bootstrap";
// import { useHistory } from "react-router-dom";
// import useSound from "use-sound";
// import io from "socket.io-client";
// import newOrderAudioNotification from "./../assets/img/newOrderNotification.mp3";
// import { getUserFromLocalStorage } from "../utilities/user.js";

// const Dashboard = () => {
//   const user = getUserFromLocalStorage();
//   const userType = user?.result?.userType;
//   const agentId = user?.result?._id;
//   const [isLoading, setIsLoading] = useState(true);
//   const [stats, setStats] = useState({
//     processingCount: 0,
//     dispatchedCount: 0,
//     assignedCount: 0,
//     deliveredCount: 0,
//     codCount: 0,
//     momoCount: 0,
//   });
//   const [trackOrderCount, setTrackOrderCount] = useState([]);
//   const [prevCount, setPrevCount] = useState(0);
//   const playbackRate = 0.95;

//   const [processingCount, setProcessingCount] = useState(0);

//   const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
//   //   console.log(audioCtx.state, "audioCtx"); //Replaced with Visual Button

//   const [play] = useSound(newOrderAudioNotification, {
//     volume: 0.2,
//     playbackRate,
//   });

//   const useDashboardStream = (agentId, setIsLoading) => {
//     const mountedRef = useRef(false);

//     const [processingCountState, setProcessingCountState] = useState(0); // Processing || New Orders Count
//     const [dispatchedCountState, setDispatchedCountState] = useState(0); // Dispatched Orders Count
//     const [assignedCountState, setAssignedCountState] = useState(0); // Assigned Orders Count
//     const [deliveredCountState, setDeliveredCountState] = useState(0); // Delivered Orders Count
//     const [codCountState, setCodCountState] = useState(0); // Delivered Orders Count
//     const [momoCountState, setMomoCountState] = useState(0); // Delivered Orders Count
//     const [trackOrderCountState, setTrackOrderCountState] = useState([]);

//     // Effect to update dashboard stats using sockets
//     useEffect(() => {
//       const socket = io(process.env.REACT_APP_SERVER_URL);

//       socket.on("connect", () => {
//         console.log("Connected to server");
//         socket.emit("joinDashboard", agentId);
//       });

//       socket.on("dashboardUpdate", (newStats) => {
//         setStats(newStats);
//         setIsLoading(false);
//         if (newStats.processingCount > prevCount) {
//           play();
//         }
//         setPrevCount(newStats.processingCount);
//       });

//       return () => {
//         socket.disconnect();
//       };
//     }, [agentId, play, prevCount]);

//     useEffect(() => {
//       setProcessingCount(processingCountState);
//       setTrackOrderCount(trackOrderCountState);
//     }, [processingCountState, trackOrderCountState]);

//     const handleMessage = useCallback((e) => {
//       if (mountedRef.current) {
//         const data = JSON.parse(e.data);
//         setTrackOrderCountState(data);
//         setIsLoading(false);

//         // Update processingCount directly
//         const processing = data.filter(
//           (order) => order?.delivery_status === "PROCESSING"
//         ).length;
//         setProcessingCountState(processing);

//         // Update dispatchedCount directly
//         const dispatched = data.filter(
//           (order) => order?.delivery_status === "DISPATCHED"
//         ).length;
//         setDispatchedCountState(dispatched);

//         // Update assignedCount directly
//         const assigned = data.filter(
//           (order) => order?.delivery_status === "ASSIGNED"
//         ).length;
//         setAssignedCountState(assigned);

//         // Update deliveredCount directly
//         const delivered = data.filter(
//           (order) => order?.delivery_status === "DELIVERED"
//         ).length;
//         setDeliveredCountState(delivered);

//         // Update codCount directly
//         const cod = data.filter(
//           (order) => order?.paymentMethod === "COD"
//         ).length;
//         setCodCountState(cod);

//         // Update momoCount directly
//         const momo = data.filter(
//           (order) => order?.paymentMethod === "MOMO"
//         ).length;
//         setMomoCountState(momo);
//       }
//     }, []);

//     useEffect(() => {
//       mountedRef.current = true;
//       const streamUrl =
//         userType === "SELLER"
//           ? `${process.env.REACT_APP_SERVER_URL}/weborders/vendor_dashboard/${agentId}`
//           : `${process.env.REACT_APP_SERVER_URL}/weborders/${agentId}/stream_dashboard`;

//       const es = new EventSource(streamUrl);
//       es.addEventListener("open", () => {
//         console.log("Dashboard stream opened!");
//       });
//       es.addEventListener("message", handleMessage);

//       return () => {
//         mountedRef.current = false;
//         es.close();
//       };
//     }, [agentId, handleMessage, userType]);

//     return [
//       trackOrderCountState,
//       processingCountState,
//       dispatchedCountState,
//       assignedCountState,
//       deliveredCountState,
//       codCountState,
//       momoCountState,
//     ];
//   };

//   const [
//     trackOrderCountState,
//     processingCountState,
//     dispatchedCountState,
//     assignedCountState,
//     deliveredCountState,
//     codCountState,
//     momoCountState,
//   ] = useDashboardStream(agentId, setIsLoading, userType);

//   //Play Sound on New Order
//   const handlePlay = useCallback(() => {
//     play();
//   }, [processingCountState]);

//   useEffect(() => {
//     if (processingCountState !== prevCount) {
//       play();
//     }
//     setPrevCount(processingCountState);
//   }, [processingCountState]);

//   const history = useHistory();

//   // Dispatched Orders Count
//   const dispatched = dispatchedCountState;

//   // Delivered Orders
//   const delivered = deliveredCountState;

//   // Assigned Orders
//   const assigned = assignedCountState;

//   // COD Payment Orders Count
//   const cod = codCountState;

//   // MoMo Payment Orders Count
//   const momo = momoCountState;

//   return (
//     <>
//       <Container fluid>
//         <Row>
//           <Col lg="3" sm="6">
//             <Card className="card-stats">
//               <Card.Body style={{ backgroundColor: "#F50076" }}>
//                 <Row>
//                   <Col xs="5">
//                     <div className="icon-big text-center icon-warning">
//                       <i className="nc-icon nc-chart text-warning"></i>
//                     </div>
//                   </Col>
//                   <Col xs="7">
//                     <div className="numbers">
//                       <p
//                         className="card-category"
//                         style={{ color: "white", textTransform: "uppercase" }}
//                       >
//                         New Order(s)
//                       </p>
//                       {isLoading ? (
//                         <p
//                           className="card-title"
//                           style={{ color: "white", fontSize: "14px" }}
//                         >
//                           processing...
//                         </p>
//                       ) : (
//                         <Card.Title
//                           as="h4"
//                           style={{
//                             fontWeight: 600,
//                             fontStyle: "italic",
//                             fontSize: 26,
//                             color: "white",
//                           }}
//                         >
//                           {stats.processingCount}
//                         </Card.Title>
//                       )}
//                     </div>
//                   </Col>
//                 </Row>
//               </Card.Body>
//               <Card.Footer>
//                 <hr></hr>
//                 <div
//                   className="stats"
//                   style={{
//                     cursor: "pointer",
//                   }}
//                   onClick={() =>
//                     history.push(
//                       user?.result?.userType === "SELLER"
//                         ? "/admin/vendorOrdersTable"
//                         : "/admin/paginatedOrdersTable"
//                     )
//                   }
//                   // onClick={() => history.push("/admin/paginatedOrdersTable")}
//                 >
//                   <i className="fas fa-redo mr-1"></i>
//                   Manage Order(s)
//                 </div>
//               </Card.Footer>
//             </Card>
//           </Col>

//           <Col lg="3" sm="6">
//             <Card className="card-stats">
//               <Card.Body style={{ backgroundColor: "#0F353D" }}>
//                 <Row>
//                   <Col xs="5">
//                     <div className="icon-big text-center icon-warning">
//                       {/* <i className="nc-icon nc-light-3 text-success"></i> */}
//                       <i className="nc-icon nc-refresh-02 text-success"></i>
//                     </div>
//                   </Col>
//                   <Col xs="7">
//                     <div className="numbers">
//                       <p
//                         className="card-category"
//                         style={{
//                           color: "white",
//                           textTransform: "uppercase",
//                         }}
//                       >
//                         Pending Delivery!
//                       </p>
//                       {isLoading ? (
//                         <p
//                           className="card-title"
//                           style={{ color: "white", fontSize: "14px" }}
//                         >
//                           processing...
//                         </p>
//                       ) : (
//                         <Card.Title
//                           as="h4"
//                           style={{
//                             fontWeight: 400,
//                             fontStyle: "italic",
//                             fontSize: 26,
//                             color: "white",
//                           }}
//                         >
//                           {" "}
//                           {stats.dispatched}
//                         </Card.Title>
//                       )}
//                     </div>
//                   </Col>
//                 </Row>
//               </Card.Body>
//               <Card.Footer>
//                 <hr></hr>
//                 <div
//                   className="stats"
//                   style={{
//                     cursor: "pointer",
//                   }}
//                   onClick={() => history.push("/admin/paginatedOrdersTable")}
//                 >
//                   <i className="far fa-calendar-alt mr-1"></i>
//                   Dispatched.
//                 </div>
//               </Card.Footer>
//               {/* Assigned Orders Count */}
//               <Card.Body style={{ backgroundColor: "#634A00" }}>
//                 <Row>
//                   <Col xs="5">
//                     <div className="icon-big text-center icon-warning">
//                       {/* Change the icon as needed */}
//                       {/* <i className="nc-icon nc-chart text-warning"></i> */}
//                       <i className="fas fa-shipping-fast text-warning"></i>
//                     </div>
//                   </Col>
//                   <Col xs="7">
//                     <div className="numbers">
//                       <p
//                         className="card-category"
//                         style={{ color: "white", textTransform: "uppercase" }}
//                       >
//                         Assigned Orders
//                       </p>
//                       {isLoading ? (
//                         <p
//                           className="card-title"
//                           style={{ color: "white", fontSize: "14px" }}
//                         >
//                           processing...
//                         </p>
//                       ) : (
//                         <Card.Title
//                           as="h4"
//                           style={{
//                             fontWeight: 600,
//                             fontStyle: "italic",
//                             fontSize: 18,
//                             color: "white",
//                           }}
//                         >
//                           {stats.assigned}
//                         </Card.Title>
//                       )}
//                     </div>
//                   </Col>
//                 </Row>
//               </Card.Body>
//               {/* End Assigned Orders Count */}
//             </Card>
//           </Col>
//           <Col lg="3" sm="6">
//             <Card className="card-stats">
//               <Card.Body style={{ backgroundColor: "#0C7D74" }}>
//                 <Row>
//                   <Col xs="5">
//                     <div className="icon-big text-center icon-warning">
//                       {/* <i className="nc-icon nc-vector text-danger"></i> */}
//                       <i className="nc-icon nc-delivery-fast text-success"></i>
//                     </div>
//                   </Col>
//                   <Col xs="7">
//                     <div className="numbers">
//                       <p
//                         className="card-category"
//                         style={{ color: "white", textTransform: "uppercase" }}
//                       >
//                         Delivered
//                       </p>
//                       {isLoading ? (
//                         <p
//                           className="card-title"
//                           style={{ color: "white", fontSize: "14px" }}
//                         >
//                           processing...
//                         </p>
//                       ) : (
//                         <Card.Title
//                           as="h4"
//                           style={{
//                             fontWeight: 600,
//                             fontStyle: "italic",
//                             fontSize: 26,
//                             color: "white",
//                           }}
//                         >
//                           {stats.delivered}
//                         </Card.Title>
//                       )}
//                     </div>
//                   </Col>
//                 </Row>
//               </Card.Body>
//               <Card.Footer>
//                 <hr></hr>
//                 <div
//                   className="stats"
//                   style={{
//                     cursor: "pointer",
//                   }}
//                   onClick={() => history.push("/admin/paginatedOrdersTable")}
//                 >
//                   {/* <i className="far fa-clock-o mr-1"></i> */}
//                   <i className="far fa-check-circle mr-1"></i>
//                   Overall Delivered.
//                 </div>
//               </Card.Footer>
//               {/* Average Delivery Time for Orders Count */}
//               <Card.Body style={{ backgroundColor: "#465973" }}>
//                 <Row>
//                   <Col xs="5">
//                     <div className="icon-big text-center icon-warning">
//                       <i className="nc-icon nc-time-alarm text-warning"></i>
//                     </div>
//                   </Col>
//                   <Col xs="7">
//                     <div className="numbers">
//                       <p
//                         className="card-category"
//                         style={{ color: "white", textTransform: "uppercase" }}
//                       >
//                         Avg Delivery Time
//                       </p>
//                       {isLoading ? (
//                         <p
//                           className="card-title"
//                           style={{ color: "white", fontSize: "14px" }}
//                         >
//                           processing...
//                         </p>
//                       ) : (
//                         <Card.Title
//                           as="h4"
//                           style={{
//                             fontWeight: 300,
//                             fontStyle: "italic",
//                             fontSize: 16,
//                             color: "white",
//                           }}
//                         >
//                           0 Hrs
//                         </Card.Title>
//                       )}
//                     </div>
//                   </Col>
//                 </Row>
//               </Card.Body>
//               {/*Average Delivery Time for Orders Orders Count */}
//             </Card>
//           </Col>
//           <Col lg="3" sm="6">
//             <Card className="card-stats">
//               {/* COD Card */}
//               <Card.Body style={{ backgroundColor: "#1C0E2E" }}>
//                 <Row>
//                   <Col xs="5">
//                     <div className="icon-big text-center icon-warning">
//                       {/* <i className="nc-icon nc-favourite-28 text-primary"></i> */}
//                       {/* <i className="fas fa-money-bill-wave text-primary"></i> */}
//                       <i className="fas fa-hand-holding-usd text-primary"></i>
//                     </div>
//                   </Col>
//                   <Col xs="7">
//                     <div className="numbers">
//                       <p className="card-category">COD</p>
//                       {isLoading ? (
//                         <p
//                           className="card-title"
//                           style={{ color: "white", fontSize: "14px" }}
//                         >
//                           processing...
//                         </p>
//                       ) : (
//                         <Card.Title
//                           as="h4"
//                           style={{
//                             fontWeight: 300,
//                             fontStyle: "italic",
//                             fontSize: 26,
//                             color: "white",
//                           }}
//                         >
//                           +{stats.cod}
//                         </Card.Title>
//                       )}
//                     </div>
//                   </Col>
//                 </Row>
//               </Card.Body>
//               {/* End COD Card */}
//               {/* Momo Card */}
//               <Card.Body style={{ backgroundColor: "#FFAE00" }}>
//                 <Row>
//                   <Col xs="5">
//                     <div className="icon-big text-center icon-warning">
//                       <i className="nc-icon nc-bank text-primary"></i>
//                     </div>
//                   </Col>
//                   <Col xs="7">
//                     <div className="numbers">
//                       <p
//                         className="card-category"
//                         style={{
//                           color: "#0F353D",
//                         }}
//                       >
//                         MoMo
//                       </p>
//                       {isLoading ? (
//                         <p className="card-title" style={{ fontSize: "14px" }}>
//                           processing...
//                         </p>
//                       ) : (
//                         <Card.Title
//                           as="h4"
//                           style={{
//                             fontWeight: 300,
//                             fontStyle: "italic",
//                             fontSize: 26,
//                             color: "white",
//                           }}
//                         >
//                           +{stats.momo}
//                         </Card.Title>
//                       )}
//                     </div>
//                   </Col>
//                 </Row>
//               </Card.Body>
//               {/* End Momo Card */}
//               <Card.Footer>
//                 <hr></hr>
//                 <div
//                   className="stats"
//                   style={{
//                     cursor: "pointer",
//                   }}
//                   onClick={() => history.push("/admin/paginatedOrdersTable")}
//                 >
//                   <i className="fas fa-redo mr-1"></i>
//                   Payment Mode
//                 </div>
//               </Card.Footer>
//             </Card>
//             <Card className="card-stats">
//               {audioCtx.state === "suspended" ? (
//                 <>
//                   <i
//                     className="nc-icon nc-bell-55 mr-1"
//                     style={{
//                       fontSize: "40px",
//                       color: "red",
//                       cursor: "pointer",
//                       marginTop: "10px",
//                       marginLeft: "10px",
//                       marginRight: "10px",
//                       marginBottom: "10px",
//                       textAlign: "center",
//                       verticalAlign: "middle",
//                     }}
//                   ></i>
//                   <Button
//                     className="btn-icon btn-danger text-white "
//                     onClick={handlePlay}
//                   >
//                     Notifications Off {""}
//                     <span
//                       role="img"
//                       //  aria-label="Person with lines near mouth"
//                       style={{
//                         fontSize: "19px",
//                         color: "white",
//                         marginLeft: "5px",
//                       }}
//                     >
//                       🔇
//                     </span>
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <i
//                     className="nc-icon nc-bell-55 mr-1"
//                     style={{
//                       fontSize: "40px",
//                       color: "green",
//                       cursor: "pointer",
//                       marginTop: "10px",
//                       marginLeft: "10px",
//                       marginRight: "10px",
//                       marginBottom: "10px",
//                       textAlign: "center",
//                       verticalAlign: "middle",
//                     }}
//                   ></i>
//                   <Button
//                     className="btn-icon btn-success text-white "
//                     onClick={handlePlay}
//                   >
//                     Notification On
//                     <span
//                       role="img"
//                       //  aria-label="Person with lines near mouth"
//                       style={{
//                         fontSize: "19px",
//                         color: "white",
//                         marginLeft: "5px",
//                       }}
//                     >
//                       🔊
//                     </span>
//                   </Button>
//                 </>
//               )}
//             </Card>
//           </Col>
//         </Row>
//         {/* <Row>
//                <Col md="12">
//                   <Card>
//                      <Card.Header>
//                         <Card.Title as="h4">
//                            Global Sales by Top Locations
//                         </Card.Title>
//                         <p className="card-category">
//                            All products that were shipped
//                         </p>
//                      </Card.Header>
//                      <Card.Body>
//                         <Row>
//                            <Col md="6">
//                               <Table responsive>
//                                  <tbody>
//                                     <tr>
//                                        <td>
//                                           <div className="flag">
//                                              <img
//                                                 alt="..."
//                                                 src={
//                                                    require('assets/img/flags/US.png')
//                                                       .default
//                                                 }
//                                              ></img>
//                                           </div>
//                                        </td>
//                                        <td>USA</td>
//                                        <td className="text-right">2.920</td>
//                                        <td className="text-right">53.23%</td>
//                                     </tr>
//                                     <tr>
//                                        <td>
//                                           <div className="flag">
//                                              <img
//                                                 alt="..."
//                                                 src={
//                                                    require('assets/img/flags/DE.png')
//                                                       .default
//                                                 }
//                                              ></img>
//                                           </div>
//                                        </td>
//                                        <td>Germany</td>
//                                        <td className="text-right">1.300</td>
//                                        <td className="text-right">20.43%</td>
//                                     </tr>
//                                     <tr>
//                                        <td>
//                                           <div className="flag">
//                                              <img
//                                                 alt="..."
//                                                 src={
//                                                    require('assets/img/flags/AU.png')
//                                                       .default
//                                                 }
//                                              ></img>
//                                           </div>
//                                        </td>
//                                        <td>Australia</td>
//                                        <td className="text-right">760</td>
//                                        <td className="text-right">10.35%</td>
//                                     </tr>
//                                     <tr>
//                                        <td>
//                                           <div className="flag">
//                                              <img
//                                                 alt="..."
//                                                 src={
//                                                    require('assets/img/flags/GB.png')
//                                                       .default
//                                                 }
//                                              ></img>
//                                           </div>
//                                        </td>
//                                        <td>United Kingdom</td>
//                                        <td className="text-right">690</td>
//                                        <td className="text-right">7.87%</td>
//                                     </tr>
//                                     <tr>
//                                        <td>
//                                           <div className="flag">
//                                              <img
//                                                 alt="..."
//                                                 src={
//                                                    require('assets/img/flags/RO.png')
//                                                       .default
//                                                 }
//                                              ></img>
//                                           </div>
//                                        </td>
//                                        <td>Romania</td>
//                                        <td className="text-right">600</td>
//                                        <td className="text-right">5.94%</td>
//                                     </tr>
//                                     <tr>
//                                        <td>
//                                           <div className="flag">
//                                              <img
//                                                 alt="..."
//                                                 src={
//                                                    require('assets/img/flags/BR.png')
//                                                       .default
//                                                 }
//                                              ></img>
//                                           </div>
//                                        </td>
//                                        <td>Brasil</td>
//                                        <td className="text-right">550</td>
//                                        <td className="text-right">4.34%</td>
//                                     </tr>
//                                  </tbody>
//                               </Table>
//                            </Col>
//                            <Col className="ml-auto mr-auto" md="6">
//                               <VectorMap
//                                  map={'world_mill'}
//                                  backgroundColor="transparent"
//                                  zoomOnScroll={false}
//                                  containerStyle={{
//                                     width: '100%',
//                                     height: '300px',
//                                  }}
//                                  containerClassName="map"
//                                  regionStyle={{
//                                     initial: {
//                                        fill: '#e4e4e4',
//                                        'fill-opacity': 0.9,
//                                        stroke: 'none',
//                                        'stroke-width': 0,
//                                        'stroke-opacity': 0,
//                                     },
//                                  }}
//                                  series={{
//                                     regions: [
//                                        {
//                                           values: {
//                                              AU: 760,
//                                              BR: 550,
//                                              CA: 120,
//                                              DE: 1300,
//                                              FR: 540,
//                                              GB: 690,
//                                              GE: 200,
//                                              IN: 200,
//                                              RO: 600,
//                                              RU: 300,
//                                              US: 2920,
//                                           },
//                                           scale: ['#AAAAAA', '#444444'],
//                                           normalizeFunction: 'polynomial',
//                                        },
//                                     ],
//                                  }}
//                               />
//                            </Col>
//                         </Row>
//                      </Card.Body>
//                   </Card>
//                </Col>
//             </Row> */}
//         {/* <Row>
//                <Col md="4">
//                   <Card>
//                      <Card.Header>
//                         <Card.Title as="h4">Email Statistics</Card.Title>
//                         <p className="card-category">
//                            Last Campaign Performance
//                         </p>
//                      </Card.Header>
//                      <Card.Body>
//                         <ChartistGraph
//                            className="ct-perfect-fourth"
//                            data={{
//                               labels: ['40%', '20%', '40%'],
//                               series: [40, 20, 40],
//                            }}
//                            type="Pie"
//                         />
//                      </Card.Body>
//                      <Card.Footer>
//                         <div className="legend">
//                            <i className="fas fa-circle mr-1 text-info"></i>
//                            Open{' '}
//                            <i className="fas fa-circle mr-1 text-danger"></i>
//                            Bounce{' '}
//                            <i className="fas fa-circle mr-1 text-warning"></i>
//                            Unsubscribe
//                         </div>
//                         <hr></hr>
//                         <div className="stats">
//                            <i className="far fa-clock-o"></i>
//                            Campaign sent 2 days ago
//                         </div>
//                      </Card.Footer>
//                   </Card>
//                </Col>
//                <Col md="8">
//                   <Card>
//                      <Card.Header>
//                         <Card.Title as="h4">Users Behavior</Card.Title>
//                         <p className="card-category">24 Hours performance</p>
//                      </Card.Header>
//                      <Card.Body>
//                         <ChartistGraph
//                            data={{
//                               labels: [
//                                  '9:00AM',
//                                  '12:00AM',
//                                  '3:00PM',
//                                  '6:00PM',
//                                  '9:00PM',
//                                  '12:00PM',
//                                  '3:00AM',
//                                  '6:00AM',
//                                  '',
//                               ],
//                               series: [
//                                  [287, 385, 490, 492, 554, 586, 698, 695, 630],
//                                  [67, 152, 143, 240, 287, 335, 435, 437, 470],
//                                  [23, 113, 67, 108, 190, 239, 307, 308, 430],
//                               ],
//                            }}
//                            type="Line"
//                            options={{
//                               low: 0,
//                               high: 800,
//                               showArea: false,
//                               height: '245px',
//                               axisX: {
//                                  showGrid: false,
//                               },
//                               lineSmooth: true,
//                               showLine: true,
//                               showPoint: true,
//                               fullWidth: true,
//                               chartPadding: {
//                                  right: 50,
//                               },
//                            }}
//                            responsiveOptions={[
//                               [
//                                  'screen and (max-width: 640px)',
//                                  {
//                                     axisX: {
//                                        labelInterpolationFnc: function (value) {
//                                           return value[0]
//                                        },
//                                     },
//                                  },
//                               ],
//                            ]}
//                         />
//                      </Card.Body>
//                      <Card.Footer>
//                         <div className="legend">
//                            <i className="fas fa-circle mr-1 text-info"></i>
//                            Open{' '}
//                            <i className="fas fa-circle mr-1 text-danger"></i>
//                            Click{' '}
//                            <i className="fas fa-circle mr-1 text-warning"></i>
//                            Click Second Time
//                         </div>
//                         <hr></hr>
//                         <div className="stats">
//                            <i className="fas fa-history"></i>
//                            Updated 3 minutes ago
//                         </div>
//                      </Card.Footer>
//                   </Card>
//                </Col>
//             </Row> */}
//         {/* <Row>
//                <Col md="6">
//                   <Card>
//                      <Card.Header>
//                         <Card.Title as="h4">2017 Sales</Card.Title>
//                         <p className="card-category">
//                            All products including Taxes
//                         </p>
//                      </Card.Header>
//                      <Card.Body>
//                         <ChartistGraph
//                            data={{
//                               labels: [
//                                  'Jan',
//                                  'Feb',
//                                  'Mar',
//                                  'Apr',
//                                  'Mai',
//                                  'Jun',
//                                  'Jul',
//                                  'Aug',
//                                  'Sep',
//                                  'Oct',
//                                  'Nov',
//                                  'Dec',
//                               ],
//                               series: [
//                                  [
//                                     542, 443, 320, 780, 553, 453, 326, 434, 568,
//                                     610, 756, 895,
//                                  ],
//                                  [
//                                     412, 243, 280, 580, 453, 353, 300, 364, 368,
//                                     410, 636, 695,
//                                  ],
//                               ],
//                            }}
//                            type="Bar"
//                            options={{
//                               seriesBarDistance: 10,
//                               axisX: {
//                                  showGrid: false,
//                               },
//                               height: '245px',
//                            }}
//                            responsiveOptions={[
//                               [
//                                  'screen and (max-width: 640px)',
//                                  {
//                                     seriesBarDistance: 5,
//                                     axisX: {
//                                        labelInterpolationFnc: function (value) {
//                                           return value[0]
//                                        },
//                                     },
//                                  },
//                               ],
//                            ]}
//                         />
//                      </Card.Body>
//                      <Card.Footer>
//                         <div className="legend">
//                            <i className="fas fa-circle mr-1 text-info"></i>
//                            Tesla Model S{' '}
//                            <i className="fas fa-circle mr-1 text-danger"></i>
//                            BMW 5 Series
//                         </div>
//                         <hr></hr>
//                         <div className="stats">
//                            <i className="fas fa-check"></i>
//                            Data information certified
//                         </div>
//                      </Card.Footer>
//                   </Card>
//                </Col>
//                <Col md="6">
//                   <Card className="card-tasks">
//                      <Card.Header>
//                         <Card.Title as="h4">Tasks</Card.Title>
//                         <p className="card-category">Backend development</p>
//                      </Card.Header>
//                      <Card.Body>
//                         <div className="table-full-width">
//                            <Table>
//                               <tbody>
//                                  <tr>
//                                     <td>
//                                        <Form.Check className="mb-1 pl-0">
//                                           <Form.Check.Label>
//                                              <Form.Check.Input
//                                                 defaultValue=""
//                                                 type="checkbox"
//                                              ></Form.Check.Input>
//                                              <span className="form-check-sign"></span>
//                                           </Form.Check.Label>
//                                        </Form.Check>
//                                     </td>
//                                     <td>
//                                        Sign contract for "What are conference
//                                        organizers afraid of?"
//                                     </td>
//                                     <td className="td-actions text-right">
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-688296980">
//                                                 Edit Task..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="info"
//                                           >
//                                              <i className="fas fa-edit"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-202192706">
//                                                 Remove..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="danger"
//                                           >
//                                              <i className="fas fa-times"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                     </td>
//                                  </tr>
//                                  <tr>
//                                     <td>
//                                        <Form.Check className="mb-1 pl-0">
//                                           <Form.Check.Label>
//                                              <Form.Check.Input
//                                                 defaultChecked
//                                                 defaultValue=""
//                                                 type="checkbox"
//                                              ></Form.Check.Input>
//                                              <span className="form-check-sign"></span>
//                                           </Form.Check.Label>
//                                        </Form.Check>
//                                     </td>
//                                     <td>
//                                        Lines From Great Russian Literature? Or
//                                        E-mails From My Boss?
//                                     </td>
//                                     <td className="td-actions text-right">
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-746544352">
//                                                 Edit Task..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="info"
//                                           >
//                                              <i className="fas fa-edit"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-743037005">
//                                                 Remove..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="danger"
//                                           >
//                                              <i className="fas fa-times"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                     </td>
//                                  </tr>
//                                  <tr>
//                                     <td>
//                                        <Form.Check className="mb-1 pl-0">
//                                           <Form.Check.Label>
//                                              <Form.Check.Input
//                                                 defaultChecked
//                                                 defaultValue=""
//                                                 type="checkbox"
//                                              ></Form.Check.Input>
//                                              <span className="form-check-sign"></span>
//                                           </Form.Check.Label>
//                                        </Form.Check>
//                                     </td>
//                                     <td>
//                                        Flooded: One year later, assessing what
//                                        was lost and what was found when a
//                                        ravaging rain swept through metro Detroit
//                                     </td>
//                                     <td className="td-actions text-right">
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-855684210">
//                                                 Edit Task..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="info"
//                                           >
//                                              <i className="fas fa-edit"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-242945902">
//                                                 Remove..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="danger"
//                                           >
//                                              <i className="fas fa-times"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                     </td>
//                                  </tr>
//                                  <tr>
//                                     <td>
//                                        <Form.Check className="mb-1 pl-0">
//                                           <Form.Check.Label>
//                                              <Form.Check.Input
//                                                 defaultChecked
//                                                 type="checkbox"
//                                              ></Form.Check.Input>
//                                              <span className="form-check-sign"></span>
//                                           </Form.Check.Label>
//                                        </Form.Check>
//                                     </td>
//                                     <td>
//                                        Create 4 Invisible User Experiences you
//                                        Never Knew About
//                                     </td>
//                                     <td className="td-actions text-right">
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-488557184">
//                                                 Edit Task..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="info"
//                                           >
//                                              <i className="fas fa-edit"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-789597281">
//                                                 Remove..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="danger"
//                                           >
//                                              <i className="fas fa-times"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                     </td>
//                                  </tr>
//                                  <tr>
//                                     <td>
//                                        <Form.Check className="mb-1 pl-0">
//                                           <Form.Check.Label>
//                                              <Form.Check.Input
//                                                 defaultValue=""
//                                                 type="checkbox"
//                                              ></Form.Check.Input>
//                                              <span className="form-check-sign"></span>
//                                           </Form.Check.Label>
//                                        </Form.Check>
//                                     </td>
//                                     <td>
//                                        Read "Following makes Medium better"
//                                     </td>
//                                     <td className="td-actions text-right">
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-521344137">
//                                                 Edit Task..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="info"
//                                           >
//                                              <i className="fas fa-edit"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-934093947">
//                                                 Remove..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="danger"
//                                           >
//                                              <i className="fas fa-times"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                     </td>
//                                  </tr>
//                                  <tr>
//                                     <td>
//                                        <Form.Check className="mb-1 pl-0">
//                                           <Form.Check.Label>
//                                              <Form.Check.Input
//                                                 defaultChecked
//                                                 defaultValue=""
//                                                 type="checkbox"
//                                              ></Form.Check.Input>
//                                              <span className="form-check-sign"></span>
//                                           </Form.Check.Label>
//                                        </Form.Check>
//                                     </td>
//                                     <td>Unfollow 5 enemies from twitter</td>
//                                     <td className="td-actions text-right">
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-97404283">
//                                                 Edit Task..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="info"
//                                           >
//                                              <i className="fas fa-edit"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                        <OverlayTrigger
//                                           overlay={
//                                              <Tooltip id="tooltip-888894273">
//                                                 Remove..
//                                              </Tooltip>
//                                           }
//                                           placement="top"
//                                        >
//                                           <Button
//                                              className="btn-simple btn-link"
//                                              type="button"
//                                              variant="danger"
//                                           >
//                                              <i className="fas fa-times"></i>
//                                           </Button>
//                                        </OverlayTrigger>
//                                     </td>
//                                  </tr>
//                               </tbody>
//                            </Table>
//                         </div>
//                      </Card.Body>
//                      <Card.Footer>
//                         <hr></hr>
//                         <div className="stats">
//                            <i className="now-ui-icons loader_refresh spin"></i>
//                            Updated 3 minutes ago
//                         </div>
//                      </Card.Footer>
//                   </Card>
//                </Col>
//             </Row> */}
//       </Container>
//     </>
//   );
// };

// export default Dashboard;
